// CoachDesk365 Help Center — Article Data + Search Utility

export type HelpCategory =
  | 'erste-schritte'
  | 'klienten'
  | 'termine'
  | 'finanzen'
  | 'methoden'
  | 'einstellungen'
  | 'integrationen'
  | 'nachrichten';

export interface HelpSection {
  heading?: string;
  paragraphs: string[];
  tip?: string;
  steps?: string[];
}

export interface HelpArticle {
  id: string;
  title: string;
  shortTitle: string;
  description: string;
  category: HelpCategory;
  icon: string;
  relatedRoute: string;
  keywords: string[];
  sections: HelpSection[];
  relatedArticles?: string[];
  estimatedMinutes?: number;
}

export const CATEGORY_LABELS: Record<HelpCategory, string> = {
  'erste-schritte': 'Erste Schritte',
  'klienten': 'Klienten',
  'termine': 'Termine',
  'finanzen': 'Finanzen',
  'methoden': 'Methoden',
  'einstellungen': 'Einstellungen',
  'integrationen': 'Integrationen',
  'nachrichten': 'Nachrichten',
};

export const CATEGORY_ICONS: Record<HelpCategory, string> = {
  'erste-schritte': 'Rocket',
  'klienten': 'Users',
  'termine': 'Calendar',
  'finanzen': 'Receipt',
  'methoden': 'BookOpen',
  'einstellungen': 'Settings',
  'integrationen': 'Plug',
  'nachrichten': 'MessageSquare',
};

export const helpArticles: HelpArticle[] = [
  // --- 1. Schnellstart ---
  {
    id: 'schnellstart',
    title: 'Schnellstart: In 5 Minuten startklar',
    shortTitle: 'Schnellstart',
    description: 'Lernen Sie die wichtigsten Funktionen kennen und starten Sie sofort mit Ihrem ersten Coaching-Prozess.',
    category: 'erste-schritte',
    icon: 'Rocket',
    relatedRoute: '/dashboard',
    keywords: ['start', 'anfangen', 'einrichten', 'tutorial', 'erste schritte', 'neu', 'loslegen', 'onboarding'],
    estimatedMinutes: 5,
    relatedArticles: ['klient-anlegen', 'sitzung-planen'],
    sections: [
      {
        heading: 'Willkommen bei CoachDesk365',
        paragraphs: [
          'CoachDesk365 ist Ihre All-in-One-Plattform, um Coaching-Prozesse professionell zu verwalten. Von der Klientenanlage bis zur Rechnungsstellung -- alles an einem Ort.',
          'Dieser Schnellstart-Guide zeigt Ihnen die wichtigsten Schritte, damit Sie sofort loslegen können.',
        ],
      },
      {
        heading: 'Schritt 1: Profil vervollständigen',
        paragraphs: ['Gehen Sie zu Mein Konto und tragen Sie Ihre Geschäftsdaten ein.'],
        steps: [
          'Navigieren Sie zu "Mein Konto" in der Seitenleiste',
          'Ergänzen Sie Firmenname, Adresse und Steuernummer',
          'Laden Sie Ihr Logo hoch (wird auf Rechnungen angezeigt)',
        ],
        tip: 'Je vollständiger Ihr Profil, desto professioneller wirken Ihre Rechnungen und Angebote.',
      },
      {
        heading: 'Schritt 2: Ersten Klienten anlegen',
        paragraphs: ['Legen Sie Ihren ersten Klienten an, um einen Coaching-Prozess zu starten.'],
        steps: [
          'Klicken Sie auf "Klienten" in der Seitenleiste',
          'Klicken Sie auf "+ Neuer Klient"',
          'Tragen Sie Name, E-Mail und Coaching-Thema ein',
        ],
      },
      {
        heading: 'Schritt 3: Erste Sitzung planen',
        paragraphs: ['Erstellen Sie Ihre erste Coaching-Sitzung direkt im Klientenprofil oder über den Kalender.'],
        steps: [
          'Öffnen Sie das Klientenprofil',
          'Wechseln Sie zum Tab "Prozess"',
          'Klicken Sie auf "Neue Sitzung"',
          'Wählen Sie Datum, Uhrzeit und Sitzungstyp',
        ],
        tip: 'Sie können auch Verfügbarkeiten einrichten, damit Klienten selbst Termine buchen.',
      },
      {
        heading: 'Nächste Schritte',
        paragraphs: [
          'Erkunden Sie die Methoden-Bibliothek für bewährte Coaching-Tools, richten Sie Ihre Buchungsseite ein, oder erstellen Sie Ihr erstes Angebot.',
        ],
      },
    ],
  },

  // --- 2. Klient anlegen ---
  {
    id: 'klient-anlegen',
    title: 'Ersten Klienten anlegen',
    shortTitle: 'Klient anlegen',
    description: 'Erstellen Sie ein Klientenprofil mit allen wichtigen Informationen für den Coaching-Prozess.',
    category: 'klienten',
    icon: 'UserPlus',
    relatedRoute: '/klienten',
    keywords: ['klient', 'anlegen', 'erstellen', 'neu', 'kontakt', 'profil', 'kunde'],
    estimatedMinutes: 3,
    relatedArticles: ['schnellstart', 'sitzung-planen'],
    sections: [
      {
        heading: 'Neuen Klienten erstellen',
        paragraphs: ['In CoachDesk365 ist jeder Klient der zentrale Ankerpunkt für Fälle, Sitzungen, Rechnungen und Dokumente.'],
        steps: [
          'Navigieren Sie zu "Klienten" in der Seitenleiste',
          'Klicken Sie auf den Button "+ Neuer Klient"',
          'Füllen Sie die Pflichtfelder aus: Vorname, Nachname, E-Mail',
          'Wählen Sie ein Coaching-Thema aus (z.B. Führungskräfte-Coaching, Work-Life-Balance)',
          'Klicken Sie auf "Speichern"',
        ],
      },
      {
        heading: 'Klienten-Details ergänzen',
        paragraphs: ['Im Klientenprofil können Sie weitere Informationen hinterlegen:'],
        steps: [
          'Telefonnummer und Adresse',
          'Unternehmen und Position',
          'Interne Notizen (nur für Sie sichtbar)',
        ],
        tip: 'Die Klienten-E-Mail wird für den Rechnungsversand und das Klienten-Portal verwendet.',
      },
      {
        heading: 'Klienten-Portal einrichten',
        paragraphs: [
          'Optional können Sie Ihrem Klienten Zugang zum Portal geben. Dort kann er Termine buchen, Rechnungen einsehen und Dokumente herunterladen.',
          'Öffnen Sie dazu den Tab "Portal" im Klientenprofil und klicken Sie auf "Einladen".',
        ],
      },
    ],
  },

  // --- 3. Sitzung planen ---
  {
    id: 'sitzung-planen',
    title: 'Sitzung planen und dokumentieren',
    shortTitle: 'Sitzung planen',
    description: 'Planen Sie Coaching-Sitzungen, dokumentieren Sie Fortschritte und verknüpfen Sie Methoden.',
    category: 'termine',
    icon: 'CalendarPlus',
    relatedRoute: '/kalender',
    keywords: ['sitzung', 'termin', 'planen', 'kalender', 'dokumentieren', 'notizen', 'protokoll'],
    estimatedMinutes: 4,
    relatedArticles: ['verfuegbarkeiten', 'methoden-nutzen'],
    sections: [
      {
        heading: 'Neue Sitzung erstellen',
        paragraphs: ['Es gibt zwei Wege, eine Sitzung anzulegen:'],
        steps: [
          'Direkt im Klientenprofil unter "Prozess" > "Neue Sitzung"',
          'Über den Quick-Action-Button "Neue Sitzung" im Dashboard',
        ],
      },
      {
        heading: 'Sitzungsdetails festlegen',
        paragraphs: ['Wählen Sie alle relevanten Parameter für die Sitzung:'],
        steps: [
          'Datum und Uhrzeit',
          'Sitzungstyp (Einzelcoaching, Paarcoaching, Gruppencoaching)',
          'Format (Online, Vor Ort, Telefon, Hybrid)',
          'Dauer in Minuten',
        ],
      },
      {
        heading: 'Sitzung dokumentieren',
        paragraphs: [
          'Nach der Sitzung können Sie direkt im Sitzungs-Block Ihre Notizen erfassen. Der Block enthält 15 strukturierte Sektionen -- von der Vorbereitung bis zur Reflexion.',
        ],
        tip: 'Ihre Entwürfe werden automatisch als Draft gespeichert. Sie gehen nicht verloren, auch wenn Sie die Seite verlassen.',
      },
      {
        heading: 'PDF-Export',
        paragraphs: [
          'Über den Download-Button im Sitzungs-Block können Sie jederzeit ein PDF Ihrer Sitzungsnotizen erstellen und herunterladen.',
        ],
      },
    ],
  },

  // --- 4. Verfügbarkeiten ---
  {
    id: 'verfuegbarkeiten',
    title: 'Verfügbarkeiten + Buchungslink',
    shortTitle: 'Verfügbarkeiten',
    description: 'Richten Sie Ihre verfügbaren Zeiten ein und teilen Sie Ihren persönlichen Buchungslink mit Klienten.',
    category: 'termine',
    icon: 'Clock',
    relatedRoute: '/einstellungen',
    keywords: ['verfügbarkeit', 'buchung', 'buchungslink', 'online buchen', 'termine', 'slots', 'zeitfenster'],
    estimatedMinutes: 4,
    relatedArticles: ['sitzung-planen', 'account-einstellungen'],
    sections: [
      {
        heading: 'Verfügbarkeiten einrichten',
        paragraphs: ['Damit Klienten eigenständig Termine buchen können, definieren Sie Ihre verfügbaren Zeitfenster.'],
        steps: [
          'Gehen Sie zu "Einstellungen" in der Seitenleiste',
          'Wählen Sie den Bereich "Buchungen / Verfügbarkeiten"',
          'Definieren Sie für jeden Wochentag Ihre verfügbaren Zeiten',
          'Legen Sie die Standard-Sitzungsdauer fest',
        ],
      },
      {
        heading: 'Buchungslink teilen',
        paragraphs: [
          'Ihr persönlicher Buchungslink hat das Format: www.coachdesk365.com/buchen/ihr-name',
          'Teilen Sie diesen Link per E-Mail, auf Ihrer Website oder in Ihrer E-Mail-Signatur.',
        ],
        tip: 'Im Kalender-Tab "Buchungen" sehen Sie alle eingegangenen Buchungen und können diese bestätigen oder ablehnen.',
      },
      {
        heading: 'Zoom-Integration',
        paragraphs: [
          'Wenn Sie Zoom verbunden haben (unter Integrationen), wird automatisch ein Meeting-Link erstellt, wenn ein Klient einen Online-Termin bucht.',
        ],
      },
    ],
  },

  // --- 5. Angebot erstellen ---
  {
    id: 'angebot-erstellen',
    title: 'Angebot erstellen und versenden',
    shortTitle: 'Angebot erstellen',
    description: 'Erstellen Sie professionelle Angebote und versenden Sie diese direkt per E-Mail an Ihre Klienten.',
    category: 'finanzen',
    icon: 'FileText',
    relatedRoute: '/rechnungen',
    keywords: ['angebot', 'erstellen', 'versenden', 'preis', 'kostenvoranschlag', 'offerte'],
    estimatedMinutes: 3,
    relatedArticles: ['rechnung-erstellen', 'datev-export'],
    sections: [
      {
        heading: 'Neues Angebot erstellen',
        paragraphs: ['Angebote helfen Ihnen, Coaching-Pakete und Preise transparent zu kommunizieren.'],
        steps: [
          'Navigieren Sie zu "Rechnungen" in der Seitenleiste',
          'Klicken Sie auf "Neues Angebot"',
          'Wählen Sie den Klienten aus',
          'Fügen Sie Positionen hinzu (Beschreibung, Menge, Einzelpreis)',
          'Prüfen Sie die Vorschau und klicken Sie auf "Speichern"',
        ],
      },
      {
        heading: 'Angebot versenden',
        paragraphs: ['Das fertige Angebot können Sie direkt als PDF per E-Mail versenden:'],
        steps: [
          'Öffnen Sie das Angebot',
          'Klicken Sie auf "Per E-Mail senden"',
          'Prüfen Sie den Betreff und die Nachricht',
          'Bestätigen Sie den Versand',
        ],
        tip: 'Das Angebot wird als professionelles PDF mit Ihrem Logo und Geschäftsdaten versendet.',
      },
      {
        heading: 'Angebot in Rechnung umwandeln',
        paragraphs: [
          'Wenn Ihr Klient das Angebot annimmt, können Sie es mit einem Klick in eine Rechnung umwandeln. Alle Positionen werden automatisch übernommen.',
        ],
      },
    ],
  },

  // --- 6. Rechnung erstellen ---
  {
    id: 'rechnung-erstellen',
    title: 'Rechnung aus Angebot erstellen',
    shortTitle: 'Rechnung erstellen',
    description: 'Wandeln Sie Angebote in Rechnungen um oder erstellen Sie Rechnungen direkt -- GoBD- und ZUGFeRD-konform.',
    category: 'finanzen',
    icon: 'Receipt',
    relatedRoute: '/rechnungen',
    keywords: ['rechnung', 'erstellen', 'faktura', 'ZUGFeRD', 'GoBD', 'mahnung', 'bezahlung', 'invoice'],
    estimatedMinutes: 4,
    relatedArticles: ['angebot-erstellen', 'datev-export'],
    sections: [
      {
        heading: 'Rechnung erstellen',
        paragraphs: ['CoachDesk365 erzeugt GoBD-konforme und ZUGFeRD-fähige Rechnungen.'],
        steps: [
          'Gehen Sie zu "Rechnungen" in der Seitenleiste',
          'Klicken Sie auf "+ Neue Rechnung" oder nutzen Sie "Aus Angebot erstellen"',
          'Wählen Sie den Klienten und fügen Sie Positionen hinzu',
          'Prüfen Sie die Vorschau',
          'Speichern und bei Bedarf direkt per E-Mail versenden',
        ],
      },
      {
        heading: 'Zahlungsstatus verfolgen',
        paragraphs: [
          'Im Rechnungs-Dashboard sehen Sie auf einen Blick: offene, überfällige, teilweise bezahlte und vollständig bezahlte Rechnungen.',
          'Teilzahlungen können Sie manuell erfassen.',
        ],
      },
      {
        heading: 'Automatisches Mahnwesen',
        paragraphs: [
          'CoachDesk365 unterstützt ein 4-Stufen-Mahnwesen. Bei überfälligen Rechnungen werden automatisch Erinnerungen verschickt.',
        ],
        tip: 'Unter Einstellungen können Sie die Zahlungsfristen und Mahntexte anpassen.',
      },
    ],
  },

  // --- 7. DATEV-Export ---
  {
    id: 'datev-export',
    title: 'DATEV-Export',
    shortTitle: 'DATEV-Export',
    description: 'Exportieren Sie Ihre Rechnungsdaten im DATEV-Format für Ihren Steuerberater.',
    category: 'finanzen',
    icon: 'Download',
    relatedRoute: '/berichte',
    keywords: ['datev', 'export', 'steuerberater', 'buchhaltung', 'CSV', 'finanzen', 'steuern'],
    estimatedMinutes: 2,
    relatedArticles: ['rechnung-erstellen'],
    sections: [
      {
        heading: 'DATEV-Export erstellen',
        paragraphs: ['Für Ihren Steuerberater können Sie alle Rechnungsdaten im DATEV-kompatiblen Format exportieren.'],
        steps: [
          'Navigieren Sie zu "Berichte" in der Seitenleiste',
          'Wählen Sie den gewünschten Zeitraum',
          'Klicken Sie auf "DATEV-Export"',
          'Die CSV-Datei wird automatisch heruntergeladen',
        ],
        tip: 'Der Export enthält alle relevanten Buchungsdaten: Rechnungsnummer, Datum, Betrag, USt-Satz und Klientenzuordnung.',
      },
      {
        heading: 'Was enthält der Export?',
        paragraphs: [
          'Der DATEV-Export enthält alle gestellten Rechnungen im gewählten Zeitraum mit Rechnungsnummer, Rechnungsdatum, Netto- und Bruttobetrag, Umsatzsteuer und Klientendaten.',
        ],
      },
    ],
  },

  // --- 8. Cloud-Speicher (BYOS) ---
  {
    id: 'cloud-speicher',
    title: 'Cloud-Speicher verbinden (BYOS)',
    shortTitle: 'Cloud-Speicher',
    description: 'Verbinden Sie Google Drive, um Dokumente automatisch in Ihrer eigenen Cloud zu speichern.',
    category: 'einstellungen',
    icon: 'Cloud',
    relatedRoute: '/integrationen',
    keywords: ['cloud', 'speicher', 'google drive', 'BYOS', 'backup', 'dokumente', 'integration', 'upload'],
    estimatedMinutes: 3,
    relatedArticles: ['account-einstellungen'],
    sections: [
      {
        heading: 'Google Drive verbinden',
        paragraphs: [
          'Mit der Google-Drive-Integration werden Rechnungen, Angebote, Verträge und Sitzungsnotizen automatisch in Ihrem Drive gespeichert. Prinzip: Bring Your Own Storage (BYOS) -- Ihre Daten bleiben in Ihrer Cloud.',
        ],
        steps: [
          'Gehen Sie zu "Integrationen" in der Seitenleiste',
          'Wechseln Sie zum Tab "Kalender"',
          'Scrollen Sie zu "Google verbinden" und tragen Sie Ihre Google Client ID und Client Secret ein',
          'Klicken Sie auf "Mit Google verbinden"',
          'Melden Sie sich mit Ihrem Google-Konto an und erteilen Sie die Berechtigung',
          'Wechseln Sie danach zum Tab "Cloud-Speicher", um die automatische Speicherung zu konfigurieren',
        ],
      },
      {
        heading: 'Automatische Ordnerstruktur',
        paragraphs: [
          'CoachDesk365 erstellt automatisch eine übersichtliche Ordnerstruktur in Ihrem Drive mit 5 Kategorien: Rechnungen, Angebote, Verträge, Dokumente und Notizen.',
        ],
        tip: 'Bereits vorhandene Dokumente werden beim Verbinden nicht rückwirkend hochgeladen. Nur neue Dokumente werden automatisch gespeichert.',
      },
    ],
  },

  // --- 9. Methoden nutzen ---
  {
    id: 'methoden-nutzen',
    title: 'Methoden in Sitzung nutzen',
    shortTitle: 'Methoden nutzen',
    description: 'Nutzen Sie über 40 bewährte Coaching-Methoden direkt in Ihren Sitzungen und erstellen Sie eigene Methoden.',
    category: 'methoden',
    icon: 'BookOpen',
    relatedRoute: '/methoden',
    keywords: ['methoden', 'tools', 'coaching-methoden', 'arbeitsblatt', 'workshop', 'übung', 'bibliothek'],
    estimatedMinutes: 3,
    relatedArticles: ['sitzung-planen'],
    sections: [
      {
        heading: 'Methoden-Bibliothek',
        paragraphs: [
          'CoachDesk365 enthält über 40 bewährte Coaching-Methoden in 15 Kategorien -- vom Rad des Lebens bis zur Aufstellungsarbeit.',
        ],
        steps: [
          'Navigieren Sie zu "Methoden" in der Seitenleiste',
          'Durchsuchen Sie die Bibliothek nach Kategorie oder Stichwort',
          'Klicken Sie auf eine Methode, um Details und Anleitung zu sehen',
          'Nutzen Sie "Arbeitsblatt erstellen" für eine druckbare Version',
        ],
      },
      {
        heading: 'Methoden im Coaching-Prozess einsetzen',
        paragraphs: [
          'Im Klientenprofil unter "Prozess" können Sie Prozessbausteine hinzufügen, die auf Methoden basieren. So dokumentieren Sie systematisch, welche Methoden Sie in welcher Sitzung eingesetzt haben.',
        ],
      },
      {
        heading: 'Eigene Methoden erstellen',
        paragraphs: [
          'Über den Methoden-Wizard können Sie auch eigene Methoden anlegen und in Ihrer persönlichen Bibliothek speichern.',
        ],
        tip: 'Im Methoden-Shop finden Sie zusätzliche Premium-Methoden, die Sie zu Ihrer Bibliothek hinzufügen können.',
      },
    ],
  },

  // --- 10. DSGVO-Export ---
  {
    id: 'dsgvo-export',
    title: 'DSGVO-Export',
    shortTitle: 'DSGVO-Export',
    description: 'Exportieren oder löschen Sie Klientendaten DSGVO-konform -- für Auskunftsersuchen und Löschungsanträge.',
    category: 'einstellungen',
    icon: 'Shield',
    relatedRoute: '/klienten',
    keywords: ['dsgvo', 'datenschutz', 'export', 'löschung', 'auskunft', 'recht auf vergessenwerden', 'privacy'],
    estimatedMinutes: 3,
    relatedArticles: ['account-einstellungen'],
    sections: [
      {
        heading: 'Klientendaten exportieren',
        paragraphs: ['Bei einem Auskunftsersuchen können Sie alle Daten eines Klienten als PDF oder CSV exportieren.'],
        steps: [
          'Öffnen Sie das Klientenprofil',
          'Klicken Sie auf das Menü (drei Punkte)',
          'Wählen Sie "DSGVO-Export"',
          'Wählen Sie das Format (PDF oder CSV)',
          'Der Export wird heruntergeladen',
        ],
        tip: 'Der Export enthält alle personenbezogenen Daten: Kontaktdaten, Sitzungen, Rechnungen und Notizen.',
      },
      {
        heading: 'Klientendaten löschen',
        paragraphs: [
          'Für das Recht auf Löschung ("Recht auf Vergessenwerden") können Sie alle Klientendaten unwiderruflich löschen.',
          'Achtung: Dieser Vorgang kann nicht rückgängig gemacht werden. Aufbewahrungspflichtige Daten (z.B. Rechnungen) bleiben gemäß gesetzlicher Vorgaben erhalten.',
        ],
      },
      {
        heading: 'Audit-Trail',
        paragraphs: [
          'Alle DSGVO-relevanten Aktionen werden im Audit-Trail protokolliert. So können Sie jederzeit nachweisen, wann welche Daten exportiert oder gelöscht wurden.',
        ],
      },
    ],
  },

  // --- 11. Account-Einstellungen ---
  {
    id: 'account-einstellungen',
    title: 'Account-Einstellungen',
    shortTitle: 'Einstellungen',
    description: 'Konfigurieren Sie Ihr Profil, Geschäftsdaten, E-Mail-Einstellungen und Ihr Design-Theme.',
    category: 'einstellungen',
    icon: 'Settings',
    relatedRoute: '/einstellungen',
    keywords: ['einstellungen', 'profil', 'konto', 'theme', 'design', 'firmenname', 'logo', 'smtp', 'email'],
    estimatedMinutes: 3,
    relatedArticles: ['passwort-sicherheit', 'cloud-speicher'],
    sections: [
      {
        heading: 'Geschäftsdaten pflegen',
        paragraphs: ['Ihre Geschäftsdaten erscheinen auf Rechnungen, Angeboten und in der Buchungsseite.'],
        steps: [
          'Gehen Sie zu "Einstellungen" in der Seitenleiste',
          'Ergänzen Sie Firmenname, Adresse, Telefon',
          'Tragen Sie Ihre Steuernummer/USt-IdNr. ein',
          'Laden Sie Ihr Logo hoch',
        ],
      },
      {
        heading: 'Design-Theme wählen',
        paragraphs: [
          'CoachDesk365 bietet 6 Themes: Boho Earth (Standard, warme Erdtöne), Golden Dusk (goldener Sonnenuntergang), Rose Quartz (sanftes Rosé), Prestige Navy (eleganter Dark Mode), Sage Forest (frisches Waldgrün) und Indigo Bloom (kräftiges Indigo).',
          'Wechseln Sie das Theme unter Einstellungen > Darstellung.',
        ],
      },
      {
        heading: 'E-Mail-Einstellungen',
        paragraphs: [
          'Konfigurieren Sie Ihre E-Mail-Absenderdaten, damit Rechnungen und Einladungen von Ihrer eigenen E-Mail-Adresse versendet werden.',
        ],
        tip: 'Prüfen Sie nach der Einrichtung den Versand mit einer Testmail an sich selbst.',
      },
    ],
  },

  // --- 12. Passwort + Sicherheit ---
  {
    id: 'passwort-sicherheit',
    title: 'Passwort + Sicherheit',
    shortTitle: 'Sicherheit',
    description: 'Ändern Sie Ihr Passwort und aktivieren Sie die Zwei-Faktor-Authentifizierung (2FA) für mehr Sicherheit.',
    category: 'einstellungen',
    icon: 'Lock',
    relatedRoute: '/mein-konto',
    keywords: ['passwort', 'sicherheit', '2FA', 'zwei-faktor', 'authentifizierung', 'login', 'backup-codes'],
    estimatedMinutes: 3,
    relatedArticles: ['account-einstellungen'],
    sections: [
      {
        heading: 'Passwort ändern',
        paragraphs: ['Ändern Sie Ihr Passwort regelmäßig für maximale Sicherheit.'],
        steps: [
          'Navigieren Sie zu "Mein Konto" in der Seitenleiste',
          'Scrollen Sie zum Bereich "Sicherheit"',
          'Geben Sie Ihr aktuelles Passwort ein',
          'Legen Sie ein neues Passwort fest (mindestens 8 Zeichen)',
          'Bestätigen Sie die Änderung',
        ],
        tip: 'Verwenden Sie ein starkes Passwort mit Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen.',
      },
      {
        heading: 'Zwei-Faktor-Authentifizierung (2FA)',
        paragraphs: [
          '2FA bietet eine zusätzliche Sicherheitsebene. Neben Ihrem Passwort benötigen Sie einen Code aus einer Authenticator-App oder per E-Mail.',
        ],
        steps: [
          'Gehen Sie zu "Mein Konto" > "Sicherheit"',
          'Klicken Sie auf "2FA aktivieren"',
          'Wählen Sie die Methode: TOTP-App (empfohlen) oder E-Mail-Code',
          'Scannen Sie den QR-Code mit Ihrer Authenticator-App',
          'Geben Sie den Bestätigungscode ein',
        ],
      },
      {
        heading: 'Backup-Codes',
        paragraphs: [
          'Beim Aktivieren von 2FA erhalten Sie Backup-Codes. Bewahren Sie diese sicher auf -- sie sind Ihr Notfall-Zugang, falls Sie Ihr Authentifizierungsgerät verlieren.',
        ],
      },
    ],
  },

  // --- 13. Nachrichten & Messaging ---
  {
    id: 'nachrichten-messaging',
    title: 'Nachrichten: Chat, WhatsApp, Teams & E-Mail',
    shortTitle: 'Nachrichten',
    description: 'Kommunizieren Sie mit Klienten über In-App-Chat, WhatsApp, Microsoft Teams und E-Mail -- alles an einem Ort.',
    category: 'nachrichten',
    icon: 'MessageSquare',
    relatedRoute: '/nachrichten',
    keywords: ['nachrichten', 'chat', 'whatsapp', 'teams', 'messaging', 'konversation', 'kommunikation', 'sms', 'email'],
    estimatedMinutes: 6,
    relatedArticles: ['whatsapp-teams-einrichten', 'klienten-portal-komplett'],
    sections: [
      {
        heading: 'Übersicht',
        paragraphs: [
          'Das Unified Messaging in CoachDesk365 vereint alle Kommunikationskanäle in einer Inbox: In-App-Chat, WhatsApp (via sipgate), Microsoft Teams und E-Mail-Benachrichtigungen.',
          'Alle eingehenden Nachrichten landen in einer zentralen Konversationsliste -- unabhängig vom Kanal.',
        ],
      },
      {
        heading: 'Konversationen verwalten',
        paragraphs: ['Die Nachrichten-Seite ist als Split-View aufgebaut: Links die Konversationsliste, rechts der Chat-Bereich.'],
        steps: [
          'Navigieren Sie zu "Nachrichten" in der Seitenleiste',
          'Klicken Sie auf eine Konversation, um den Chat zu öffnen',
          'Schreiben Sie eine Nachricht und drücken Sie Enter zum Senden',
          'Über den "+ Button" starten Sie eine neue Konversation mit einem Klienten',
        ],
      },
      {
        heading: 'Auto-Replies (Automatische Antworten)',
        paragraphs: ['CoachDesk365 bietet 3 Auto-Reply-Modi, die eingehende Nachrichten automatisch beantworten:'],
        steps: [
          '"Im Coaching" -- Aktivieren Sie diesen Modus während einer Sitzung, damit Klienten eine Info-Nachricht erhalten',
          '"Geschäftszeiten" -- Automatische Antwort außerhalb Ihrer definierten Zeiten',
          '"Urlaub" -- Abwesenheitsnachricht für längere Zeiträume',
        ],
        tip: 'Den "Im Coaching"-Modus können Sie direkt über den Button in der Nachrichten-Seitenleiste aktivieren/deaktivieren.',
      },
      {
        heading: 'Archivierung',
        paragraphs: [
          'Konversationen können manuell archiviert oder automatisch archiviert werden (bei Fall-Abschluss oder nach Inaktivität). Archivierte Konversationen bleiben gespeichert und können jederzeit wiederhergestellt werden.',
          'Unter "Archiv-Einstellungen" können Sie die Aufbewahrungsdauer (Standard: 24 Monate) und die automatische Archivierung konfigurieren.',
        ],
      },
      {
        heading: 'Echtzeit-Updates',
        paragraphs: [
          'Neue Nachrichten erscheinen sofort per WebSocket -- kein Neuladen nötig. Falls die WebSocket-Verbindung unterbrochen wird, greift automatisch ein 10-Sekunden-Polling als Fallback.',
        ],
      },
      {
        heading: 'Dashboard-Widget',
        paragraphs: [
          'Auf dem Dashboard sehen Sie die letzten 5 Konversationen mit Unread-Badges. Ein Klick führt direkt zur Nachrichten-Seite.',
        ],
      },
    ],
  },

  // --- 14. WhatsApp & Teams einrichten ---
  {
    id: 'whatsapp-teams-einrichten',
    title: 'WhatsApp & Teams: Einrichtung Schritt für Schritt',
    shortTitle: 'WhatsApp & Teams',
    description: 'Richten Sie WhatsApp (via sipgate) und Microsoft Teams als Kommunikationskanäle für Ihre Klienten ein.',
    category: 'integrationen',
    icon: 'Smartphone',
    relatedRoute: '/integrationen',
    keywords: ['whatsapp', 'teams', 'sipgate', 'sms', 'bot', 'azure', 'kanal', 'channel', 'webhook'],
    estimatedMinutes: 8,
    relatedArticles: ['nachrichten-messaging', 'integrationen-uebersicht'],
    sections: [
      {
        heading: 'WhatsApp/SMS via sipgate',
        paragraphs: [
          'CoachDesk365 nutzt sipgate als Anbieter für WhatsApp und SMS. sipgate ist ein deutscher Anbieter (Kosten: ca. 3,95 EUR/Monat für eine Coaching-Nummer). Sie benötigen ein sipgate-Konto mit API-Zugang.',
        ],
        steps: [
          'Erstellen Sie ein sipgate-Konto unter sipgate.de (falls noch nicht vorhanden)',
          'Aktivieren Sie den API-Zugang in Ihrem sipgate-Dashboard',
          'Notieren Sie sich: Token-ID, Token und Ihre SMS-Erweiterungs-ID (z.B. "s0")',
          'In CoachDesk365: Gehen Sie zu "Integrationen" > Tab "Messaging"',
          'Tragen Sie Token-ID, Token und SMS-ID ein',
          'Optional: Tragen Sie eine Testnummer ein und klicken Sie "Testen"',
          'Aktivieren Sie den Kanal mit dem Schalter',
        ],
      },
      {
        heading: 'sipgate Webhook konfigurieren',
        paragraphs: [
          'Damit eingehende SMS/WhatsApp-Nachrichten von sipgate an CoachDesk365 weitergeleitet werden, müssen Sie einen Webhook in Ihrem sipgate-Dashboard einrichten.',
        ],
        steps: [
          'Öffnen Sie Ihr sipgate-Dashboard > Einstellungen > Webhooks',
          'Fügen Sie einen neuen Webhook hinzu',
          'URL: https://api.coachdesk365.com/api/v1/channels/webhooks/sipgate',
          'Event: "newSms"',
          'Speichern Sie den Webhook',
        ],
        tip: 'Eingehende Nachrichten werden automatisch dem richtigen Klienten zugeordnet (anhand der Telefonnummer im Klientenprofil).',
      },
      {
        heading: 'Microsoft Teams (Bot Framework)',
        paragraphs: [
          'Für die Teams-Integration benötigen Sie eine Azure-Bot-Registrierung. Dies ermöglicht es Ihren Business-Klienten, Ihnen direkt über Teams zu schreiben.',
        ],
        steps: [
          'Erstellen Sie eine Bot-Registrierung im Azure Portal (portal.azure.com)',
          'Notieren Sie sich die App-ID (Client ID) und das App-Secret',
          'Konfigurieren Sie den Messaging-Endpunkt: https://api.coachdesk365.com/api/v1/channels/webhooks/teams',
          'In CoachDesk365: Gehen Sie zu "Integrationen" > Tab "Messaging"',
          'Tragen Sie App-ID und App-Secret im Bereich "Microsoft Teams" ein',
          'Klicken Sie auf "Testen" um die Verbindung zu prüfen',
          'Aktivieren Sie den Kanal',
        ],
      },
      {
        heading: 'Kanalstrategie',
        paragraphs: [
          'Empfohlene Aufteilung: WhatsApp für private Klienten (persönlich, direkt), Teams für Business-Klienten (professionell, Unternehmenskontext), In-App-Chat für alle (immer verfügbar), E-Mail als Fallback.',
        ],
      },
    ],
  },

  // --- 15. Integrationen Übersicht ---
  {
    id: 'integrationen-uebersicht',
    title: 'Alle Integrationen im Überblick',
    shortTitle: 'Integrationen',
    description: 'Google Drive, Zoom, Kalender-Sync, Stripe, SMTP und mehr -- so verbinden Sie CoachDesk365 mit Ihren Tools.',
    category: 'integrationen',
    icon: 'Plug',
    relatedRoute: '/integrationen',
    keywords: ['integration', 'verbinden', 'google', 'zoom', 'stripe', 'smtp', 'kalender', 'sync', 'outlook', 'ical'],
    estimatedMinutes: 10,
    relatedArticles: ['cloud-speicher', 'whatsapp-teams-einrichten', 'stripe-zahlungen'],
    sections: [
      {
        heading: 'Übersicht: 9 Integrationen',
        paragraphs: [
          'CoachDesk365 bietet 9 vollständige Integrationen. Alle werden unter "Integrationen" in der Seitenleiste konfiguriert, aufgeteilt in 6 Tabs: E-Mail, Kalender, Video, Cloud, Messaging und Webhooks.',
        ],
      },
      {
        heading: 'Google Drive (Cloud-Speicher)',
        paragraphs: [
          'Automatischer Upload von Rechnungen, Angeboten, Verträgen, Dokumenten und Sitzungsnotizen in Ihr Google Drive. Pro Kategorie kann ein eigener Zielordner gewählt werden.',
        ],
        steps: [
          'Integrationen > Tab "Kalender" > Google verbinden (OAuth)',
          'Dann Tab "Cloud" > Auto-Upload-Kategorien aktivieren',
          'Zielordner pro Kategorie wählen',
        ],
        tip: 'Google Drive und Google Kalender nutzen dieselbe OAuth-Verbindung -- einmal verbinden reicht für beides.',
      },
      {
        heading: 'Google Kalender',
        paragraphs: [
          'Bidirektionale Synchronisation: Coaching-Sitzungen werden automatisch in Ihren Google Kalender übertragen und umgekehrt. Klienten können optional als Teilnehmer eingeladen werden.',
        ],
        steps: [
          'Integrationen > Tab "Kalender" > Google verbinden',
          'Auto-Sync und Klienten-Einladung aktivieren',
          '"Jetzt synchronisieren" für manuellen Sync',
        ],
      },
      {
        heading: 'Outlook Kalender',
        paragraphs: [
          'One-Way-Sync: Coaching-Sitzungen werden in Ihren Outlook-Kalender übertragen (90-Tage-Fenster). Events werden mit der Kategorie "CoachDesk365" markiert.',
        ],
        steps: [
          'Integrationen > Tab "Kalender" > Outlook verbinden',
          'Azure AD Client-ID und Secret eintragen',
          'OAuth-Autorisierung durchführen',
        ],
      },
      {
        heading: 'Zoom (Videokonferenzen)',
        paragraphs: [
          'Automatische Meeting-Link-Erstellung für Online-Sitzungen. Nutzt Server-to-Server OAuth (kein Browser-Redirect nötig).',
        ],
        steps: [
          'Erstellen Sie eine Server-to-Server App im Zoom Marketplace',
          'Integrationen > Tab "Video" > Account ID, Client ID, Client Secret eintragen',
          '"Auto-Create Meetings" aktivieren',
        ],
      },
      {
        heading: 'SMTP (E-Mail-Versand)',
        paragraphs: [
          'Konfigurieren Sie Ihren eigenen SMTP-Server, damit Rechnungen, Erinnerungen und Einladungen von Ihrer geschäftlichen E-Mail-Adresse versendet werden. 10 anpassbare E-Mail-Templates.',
        ],
        steps: [
          'Einstellungen > Tab "E-Mail"',
          'SMTP-Server, Port, Benutzername, Passwort eingeben',
          'Absendername und -adresse festlegen',
          'Testmail senden',
        ],
      },
      {
        heading: 'iCal-Feed',
        paragraphs: [
          'Ein geschützter iCal-Feed-Link wird automatisch generiert. Damit können Sie Ihre Coaching-Termine in Apple Kalender, Thunderbird oder anderen Kalender-Apps abonnieren (read-only).',
        ],
      },
      {
        heading: 'Stripe (Online-Zahlungen)',
        paragraphs: [
          'Klienten können Rechnungen direkt über das Portal online bezahlen. Stripe wickelt die Zahlung ab und der Status wird automatisch aktualisiert.',
        ],
        tip: 'Details zur Stripe-Einrichtung finden Sie im Artikel "Online-Zahlungen mit Stripe".',
      },
    ],
  },

  // --- 16. Stripe & Online-Zahlungen ---
  {
    id: 'stripe-zahlungen',
    title: 'Online-Zahlungen mit Stripe',
    shortTitle: 'Stripe-Zahlungen',
    description: 'Ermöglichen Sie Klienten, Rechnungen online über das Portal zu bezahlen -- mit Kreditkarte, SEPA oder Apple Pay.',
    category: 'finanzen',
    icon: 'CreditCard',
    relatedRoute: '/integrationen',
    keywords: ['stripe', 'zahlung', 'online', 'kreditkarte', 'bezahlen', 'payment', 'sepa', 'portal'],
    estimatedMinutes: 5,
    relatedArticles: ['rechnung-erstellen', 'klienten-portal-komplett'],
    sections: [
      {
        heading: 'Stripe einrichten',
        paragraphs: [
          'Stripe ist der Zahlungsanbieter für Online-Zahlungen im Klienten-Portal. Die Einrichtung erfolgt über Umgebungsvariablen auf dem Server.',
        ],
        steps: [
          'Erstellen Sie ein Stripe-Konto unter stripe.com',
          'Notieren Sie sich den Secret Key und den Publishable Key (aus dem Dashboard)',
          'Erstellen Sie einen Webhook-Endpunkt: https://api.coachdesk365.com/api/v1/payments/webhook/stripe',
          'Events: payment_intent.succeeded, payment_intent.payment_failed, checkout.session.completed',
          'Notieren Sie sich das Webhook-Signing-Secret',
        ],
      },
      {
        heading: 'Zahlungsablauf',
        paragraphs: [
          'Wenn ein Klient im Portal eine Rechnung öffnet, sieht er einen "Online bezahlen"-Button. Dieser startet einen Stripe-Zahlungsvorgang (PaymentIntent). Nach erfolgreicher Zahlung wird die Rechnung automatisch als "bezahlt" markiert.',
        ],
        tip: 'Zum Testen verwenden Sie die Stripe-Testkarten (z.B. 4242 4242 4242 4242).',
      },
      {
        heading: 'Unterstützte Zahlungsmethoden',
        paragraphs: [
          'Stripe unterstützt: Kreditkarten (Visa, Mastercard, Amex), SEPA-Lastschrift, Apple Pay, Google Pay und weitere lokale Zahlungsmethoden -- je nach Konfiguration in Ihrem Stripe-Dashboard.',
        ],
      },
    ],
  },

  // --- 17. Klienten-Portal komplett ---
  {
    id: 'klienten-portal-komplett',
    title: 'Klienten-Portal: Einladung bis Nutzung',
    shortTitle: 'Klienten-Portal',
    description: 'So nutzen Ihre Klienten das Portal -- von der Einladung über die Registrierung bis zu Rechnungen, Terminen und Chat.',
    category: 'klienten',
    icon: 'UserCheck',
    relatedRoute: '/portal',
    keywords: ['portal', 'klient', 'einladung', 'registrierung', 'online', 'self-service', 'termin buchen', 'rechnung bezahlen'],
    estimatedMinutes: 6,
    relatedArticles: ['klient-anlegen', 'stripe-zahlungen', 'nachrichten-messaging'],
    sections: [
      {
        heading: 'Klienten einladen',
        paragraphs: ['Sie laden Klienten zum Portal ein, damit diese eigenständig Termine buchen, Rechnungen einsehen und mit Ihnen chatten können.'],
        steps: [
          'Öffnen Sie das Klientenprofil',
          'Wechseln Sie zum Tab "Portal"',
          'Klicken Sie auf "Zum Portal einladen"',
          'Der Klient erhält eine E-Mail mit einem Setup-Link + Zugangscode',
        ],
        tip: 'Der Setup-Link ist 7 Tage gültig. Falls er abläuft, können Sie die Einladung erneut senden.',
      },
      {
        heading: 'Klienten-Registrierung',
        paragraphs: [
          'Der Klient klickt den Setup-Link und gelangt zur Registrierungsseite. Dort sieht er seinen Namen und eine maskierte E-Mail zur Bestätigung. Er muss ein Passwort festlegen (mindestens 8 Zeichen, mit Stärke-Anzeige).',
          'Nach der Registrierung erhält der Klient eine Bestätigungs-E-Mail mit seinen Zugangsdaten.',
        ],
      },
      {
        heading: 'Portal-Funktionen für Klienten',
        paragraphs: ['Das Portal bietet folgende Self-Service-Funktionen:'],
        steps: [
          'Dashboard -- Übersicht über anstehende Termine und offene Rechnungen',
          'Termine -- Kalender-Ansicht mit Buchungsmöglichkeit für freie Slots',
          'Sitzungen -- Einsicht in vergangene Sitzungen (ohne Coach-Notizen)',
          'Rechnungen -- Alle Rechnungen mit PDF-Vorschau und Online-Zahlung (Stripe)',
          'Dokumente -- Freigegebene Dokumente herunterladen',
          'Chat -- Direkte Kommunikation mit dem Coach',
          'Profil -- Kontaktdaten aktualisieren, Änderungsanträge stellen',
          'DSGVO -- Datenauskunft und Löschung beantragen',
        ],
      },
      {
        heading: 'White Label / Branding',
        paragraphs: [
          'Das Portal zeigt Ihren Firmennamen (business_name) statt "CoachDesk365" an -- sowohl im Portal-Header als auch auf der Buchungsseite. So wirkt das Portal wie Ihre eigene Plattform.',
        ],
      },
      {
        heading: 'Änderungsanträge',
        paragraphs: [
          'Klienten können über das Portal Änderungen an ihren Stammdaten beantragen (z.B. Namensänderung, neue Adresse). Der Antrag landet in Ihrem Support-Bereich und kann dort genehmigt oder abgelehnt werden. Bei Genehmigung werden die Daten automatisch aktualisiert.',
        ],
      },
    ],
  },

  // --- 18. Team-Management ---
  {
    id: 'team-management',
    title: 'Team-Management: Praxis & Mitarbeiter',
    shortTitle: 'Team-Management',
    description: 'Verwalten Sie Ihre Coaching-Praxis mit Teammitgliedern, Rollen und Berechtigungen.',
    category: 'einstellungen',
    icon: 'UsersRound',
    relatedRoute: '/einstellungen',
    keywords: ['team', 'praxis', 'mitarbeiter', 'rollen', 'berechtigungen', 'einladung', 'inhaber', 'admin', 'coach', 'assistenz'],
    estimatedMinutes: 5,
    relatedArticles: ['account-einstellungen'],
    sections: [
      {
        heading: 'Praxis erstellen',
        paragraphs: [
          'Unter Einstellungen > Tab "Team" können Sie eine Praxis erstellen. Sie werden automatisch als Inhaber eingetragen. Danach können Sie Teammitglieder einladen.',
        ],
        steps: [
          'Navigieren Sie zu "Einstellungen"',
          'Wechseln Sie zum Tab "Team"',
          'Klicken Sie auf "Praxis erstellen"',
          'Geben Sie den Praxisnamen und optional eine Beschreibung ein',
        ],
      },
      {
        heading: '4 Rollen',
        paragraphs: ['Jedes Teammitglied hat eine Rolle, die bestimmte Basisrechte definiert:'],
        steps: [
          'Inhaber -- Vollzugriff, kann nicht entfernt werden, einmalig pro Praxis',
          'Admin -- Vollzugriff auf Verwaltungsfunktionen',
          'Coach -- Arbeit mit eigenen Klienten und Sitzungen',
          'Assistenz -- Eingeschränkter Zugriff (z.B. nur Termine verwalten)',
        ],
      },
      {
        heading: '4 Berechtigungen',
        paragraphs: ['Zusätzlich zur Rolle können 4 granulare Berechtigungen individuell vergeben werden:'],
        steps: [
          'Klienten anlegen -- Darf neue Klienten erstellen',
          'Mitglieder verwalten -- Darf Team-Einstellungen ändern und Einladungen versenden',
          'Berichte einsehen -- Zugriff auf Dashboard-Statistiken und Reports',
          'Rechnungen verwalten -- Darf Rechnungen erstellen und versenden',
        ],
      },
      {
        heading: 'Mitglieder einladen',
        paragraphs: ['Neue Teammitglieder werden per E-Mail eingeladen. Die Einladung ist 7 Tage gültig.'],
        steps: [
          'Im Team-Tab: Geben Sie die E-Mail-Adresse ein',
          'Wählen Sie Rolle und Berechtigungen',
          'Klicken Sie auf "Einladen"',
          'Der Eingeladene erhält eine E-Mail mit Annahme-Link',
          'Nach Registrierung/Login kann die Einladung angenommen werden',
        ],
        tip: 'Die maximale Anzahl an Mitgliedern hängt von Ihrem Abo-Plan ab. Bei Bedarf können Sie Ihr Abo upgraden.',
      },
    ],
  },

  // --- 19. Qualitätsmanagement ---
  {
    id: 'qualitaetsmanagement',
    title: 'Qualitätsmanagement (ISO 9001)',
    shortTitle: 'QM ISO 9001',
    description: 'Dokumentieren Sie Ihre Coaching-Qualität nach ISO 9001 -- mit Richtlinien, Verbesserungen und Audit-Trail.',
    category: 'methoden',
    icon: 'Shield',
    relatedRoute: '/qualitaet',
    keywords: ['qualität', 'QM', 'ISO', '9001', 'audit', 'verbesserung', 'richtlinie', 'qualitätsmanagement'],
    estimatedMinutes: 4,
    relatedArticles: ['schnellstart'],
    sections: [
      {
        heading: 'Übersicht',
        paragraphs: [
          'Das QM-Modul hilft Ihnen, Ihre Coaching-Qualität systematisch zu dokumentieren und zu verbessern -- angelehnt an ISO 9001. Sie können Richtlinien erstellen, Verbesserungsmaßnahmen dokumentieren und einen Audit-Trail führen.',
        ],
      },
      {
        heading: 'Richtlinien & Prozesse',
        paragraphs: ['Dokumentieren Sie Ihre Coaching-Standards:'],
        steps: [
          'Navigieren Sie zu "Qualität" in der Seitenleiste',
          'Erstellen Sie Richtlinien (z.B. "Sitzungsvorbereitung", "Klientenschutz")',
          'Definieren Sie Prozessbeschreibungen und Verantwortlichkeiten',
        ],
      },
      {
        heading: 'Verbesserungsmaßnahmen',
        paragraphs: [
          'Halten Sie Verbesserungsvorschläge und deren Umsetzung fest. Jede Maßnahme hat einen Status (offen, in Bearbeitung, abgeschlossen) und kann priorisiert werden.',
        ],
      },
      {
        heading: 'Audit-Trail',
        paragraphs: [
          'Alle qualitätsrelevanten Aktionen werden automatisch protokolliert. So können Sie bei einer Zertifizierung oder Überprüfung jederzeit nachweisen, welche Maßnahmen wann durchgeführt wurden.',
        ],
      },
    ],
  },

  // --- 20. Dashboard & Berichte ---
  {
    id: 'dashboard-berichte',
    title: 'Dashboard & Berichte',
    shortTitle: 'Dashboard & Berichte',
    description: 'Verstehen Sie Ihre KPIs, Umsatzentwicklung und Coaching-Aktivitäten auf einen Blick.',
    category: 'erste-schritte',
    icon: 'BarChart3',
    relatedRoute: '/dashboard',
    keywords: ['dashboard', 'berichte', 'statistik', 'umsatz', 'KPI', 'chart', 'übersicht', 'trend', 'report'],
    estimatedMinutes: 4,
    relatedArticles: ['schnellstart', 'datev-export'],
    sections: [
      {
        heading: 'Dashboard',
        paragraphs: ['Das Dashboard ist Ihre Startseite und zeigt alle wichtigen Informationen auf einen Blick:'],
        steps: [
          '4 KPI-Karten -- Aktive Klienten, Sitzungen diesen Monat, Offene Rechnungen, Monatsumsatz (jeweils mit Trend zum Vormonat)',
          'Quick-Actions -- Schnellzugriff für neue Sitzung, Rechnung, Klient, Termin',
          'Umsatz-Chart -- Interaktives Diagramm mit Umsatz + Sitzungen (3/6/12 Monate umschaltbar)',
          'Offene Aufgaben -- Überfällige Rechnungen, Follow-Up-Antworten, offene Verträge',
          'Nachrichten -- Die 5 neuesten Konversationen mit Unread-Badges',
          'Nächste Sitzungen -- Anstehende Termine mit Klient, Uhrzeit und Sitzungstyp',
          'Letzte Aktivitäten -- Timeline aller Aktionen (Klient angelegt, Rechnung erstellt etc.)',
        ],
      },
      {
        heading: 'Berichte',
        paragraphs: ['Unter "Berichte" in der Seitenleiste finden Sie detaillierte Auswertungen:'],
        steps: [
          'Umsatz-Report -- Monatliche und jährliche Umsatzübersicht',
          'Klienten-Report -- Aktive/neue Klienten im Zeitverlauf',
          'Sitzungs-Report -- Sitzungsanzahl nach Typ und Monat',
          'DATEV-Export -- CSV-Download für Ihren Steuerberater',
        ],
      },
      {
        heading: 'Trend-Badges',
        paragraphs: [
          'Die KPI-Karten zeigen Trend-Badges: Grüner Pfeil nach oben = Verbesserung zum Vormonat, roter Pfeil nach unten = Rückgang. So sehen Sie sofort, wie sich Ihre Praxis entwickelt.',
        ],
      },
    ],
  },

  // --- 21. Mahnwesen ---
  {
    id: 'mahnwesen',
    title: 'Mahnwesen: 4-Stufen-System',
    shortTitle: 'Mahnwesen',
    description: 'Automatisches Mahnwesen mit 4 Eskalationsstufen, E-Mail-Templates und KPI-Dashboard.',
    category: 'finanzen',
    icon: 'AlertTriangle',
    relatedRoute: '/rechnungen',
    keywords: ['mahnung', 'mahnwesen', 'dunning', 'überfällig', 'zahlungserinnerung', 'inkasso', 'eskalation'],
    estimatedMinutes: 4,
    relatedArticles: ['rechnung-erstellen', 'stripe-zahlungen'],
    sections: [
      {
        heading: '4 Mahnstufen',
        paragraphs: ['CoachDesk365 unterstützt ein 4-stufiges Mahnwesen mit steigender Eskalation:'],
        steps: [
          'Stufe 1 -- Freundliche Zahlungserinnerung (z.B. 7 Tage nach Fälligkeit)',
          'Stufe 2 -- 1. Mahnung mit Fristsetzung',
          'Stufe 3 -- 2. Mahnung mit Androhung von Konsequenzen',
          'Stufe 4 -- Letzte Mahnung / Inkasso-Ankündigung',
        ],
      },
      {
        heading: 'Automatischer Versand',
        paragraphs: [
          'Mahnungen können automatisch per E-Mail versendet werden. Die E-Mail-Templates sind anpassbar unter Einstellungen > E-Mail-Templates.',
          'Sie können auch manuell mahnen: Öffnen Sie die Rechnung und klicken Sie auf "Mahnung senden".',
        ],
        tip: 'Im Rechnungs-Dashboard sehen Sie auf einen Blick, welche Rechnungen überfällig sind und in welcher Mahnstufe sie sich befinden.',
      },
      {
        heading: 'KPI-Dashboard',
        paragraphs: [
          'Das Mahnwesen-Dashboard zeigt: Gesamtsumme offener Forderungen, Anzahl nach Mahnstufe, durchschnittliche Zahlungsdauer und Erfolgsquote der Mahnungen.',
        ],
      },
    ],
  },

  // --- 22. Onboarding für neue Nutzer ---
  {
    id: 'onboarding-guide',
    title: 'Onboarding: Einrichtungs-Assistent & Demo',
    shortTitle: 'Onboarding',
    description: 'Der 6-Schritte-Assistent richtet Ihre Praxis ein, lädt Demo-Daten und zeigt Ihnen alle Funktionen.',
    category: 'erste-schritte',
    icon: 'Rocket',
    relatedRoute: '/dashboard',
    keywords: ['onboarding', 'wizard', 'einrichtung', 'demo', 'demo-daten', 'erste schritte', 'setup', 'tour'],
    estimatedMinutes: 3,
    relatedArticles: ['schnellstart', 'dashboard-berichte'],
    sections: [
      {
        heading: 'Der Einrichtungs-Assistent',
        paragraphs: [
          'Beim ersten Login wird automatisch ein 6-Schritte-Assistent angezeigt, der Sie durch die Grundeinrichtung führt. Er dauert nur 2 Minuten.',
        ],
      },
      {
        heading: 'Die 6 Schritte',
        paragraphs: [],
        steps: [
          'Willkommen -- Begrüßung und Start',
          'Profil -- Vorname, Nachname und Firmenname eingeben',
          'Branding -- Logo hochladen (optional, kann übersprungen werden)',
          'Team -- Solo-Coach oder Praxis/Team einrichten, Kollegen einladen (optional)',
          'Erster Klient -- Direkt Ihren ersten Klienten anlegen (optional)',
          'Fertig -- Demo-Daten laden (empfohlen) und Free Trial starten',
        ],
      },
      {
        heading: 'Demo-Daten',
        paragraphs: [
          'Im letzten Schritt können Sie Demo-Daten laden lassen (Checkbox, standardmäßig aktiv). Die Demo-Daten umfassen 5 Beispiel-Klienten, 5 Fälle, 15 Sitzungen, 6 Rechnungen und 3 Angebote -- so können Sie alle Funktionen sofort ausprobieren.',
          'Auf dem Dashboard erscheint dann ein gelbes "Demo-Modus"-Banner mit einem Button zum Löschen aller Demo-Daten, sobald Sie mit echten Daten arbeiten möchten.',
        ],
        tip: 'Demo-Daten können jederzeit mit einem Klick gelöscht werden. Ihre echten Daten bleiben dabei vollständig erhalten.',
      },
      {
        heading: 'Onboarding-Banner & Demo-Tour',
        paragraphs: [
          'Nach dem Assistenten zeigt das Dashboard 14 Tage lang ein Onboarding-Banner mit zwei Links: "Schnellstart" (führt zu diesem Hilfe-Artikel) und "Demo" (öffnet eine interaktive 7-Slide-Tour durch alle Hauptfunktionen).',
          'Das Banner kann jederzeit geschlossen werden und erscheint dann nicht mehr.',
        ],
      },
    ],
  },

  // --- 23. Stornierung von Rechnungen ---
  {
    id: 'storno-rechnung',
    title: 'Rechnung stornieren (Stornorechnung)',
    shortTitle: 'Storno',
    description: 'So erstellen Sie eine Stornorechnung und buchen fehlerhafte oder abgesagte Rechnungen korrekt zurück.',
    category: 'finanzen',
    icon: 'Receipt',
    relatedRoute: '/rechnungen',
    keywords: ['storno', 'stornierung', 'stornorechnung', 'gutschrift', 'rechnung stornieren', 'abbrechen', 'rückgängig', 'korrektur', 'korrekturbuchung', 'annullieren'],
    estimatedMinutes: 4,
    relatedArticles: ['rechnung-erstellen', 'mahnwesen', 'datev-export'],
    sections: [
      {
        heading: 'Wann stornieren?',
        paragraphs: [
          'Eine Rechnung muss storniert werden, wenn sie fehlerhaft ist (z.B. falscher Betrag, falscher Klient) oder wenn eine Leistung nicht erbracht wurde. Nach deutschem Recht dürfen Rechnungen nicht einfach gelöscht werden -- stattdessen wird eine Stornorechnung erstellt.',
          'Die Stornorechnung erhält eine eigene Rechnungsnummer und referenziert die Originalrechnung. Der Betrag wird negativ ausgewiesen.',
        ],
      },
      {
        heading: 'Rechnung stornieren',
        paragraphs: ['So erstellen Sie eine Stornorechnung:'],
        steps: [
          'Navigieren Sie zu "Rechnungen" in der Seitenleiste',
          'Öffnen Sie die zu stornierende Rechnung',
          'Klicken Sie auf das Drei-Punkte-Menü (⋮) oder den Button "Stornieren"',
          'Bestätigen Sie die Stornierung -- es wird automatisch eine Stornorechnung mit negativem Betrag erstellt',
          'Optional: Geben Sie einen Stornogrund ein',
        ],
        tip: 'Eine Stornorechnung kann nicht rückgängig gemacht werden. Prüfen Sie vorher sorgfältig, ob die richtige Rechnung ausgewählt ist.',
      },
      {
        heading: 'Was passiert nach der Stornierung?',
        paragraphs: [
          'Die Originalrechnung wird als "storniert" markiert und erscheint durchgestrichen in der Liste. Die Stornorechnung wird mit dem Vermerk "Storno zu RE-XXXX" erstellt.',
          'Beide Rechnungen bleiben für die Buchhaltung erhalten. Der Umsatz im Dashboard und in Berichten wird automatisch korrigiert.',
        ],
      },
      {
        heading: 'Teilstorno',
        paragraphs: [
          'Wenn nur ein Teil des Betrags storniert werden soll, erstellen Sie stattdessen eine Korrekturrechnung. Stornieren Sie die Originalrechnung komplett und erstellen Sie eine neue Rechnung mit dem korrekten Betrag.',
        ],
        tip: 'Im DATEV-Export werden Stornorechnungen automatisch korrekt erfasst -- Sie müssen nichts manuell anpassen.',
      },
    ],
  },

  // --- 24. Fälle verwalten ---
  {
    id: 'faelle-verwalten',
    title: 'Fälle verwalten: Coaching-Prozesse strukturieren',
    shortTitle: 'Fälle',
    description: 'Fälle bündeln Sitzungen und Bausteine zu einem Coaching-Prozess. So organisieren Sie Ihre Arbeit.',
    category: 'klienten',
    icon: 'Layers',
    relatedRoute: '/klienten',
    keywords: ['fall', 'fälle', 'case', 'coaching-prozess', 'prozessmappe', 'baum', 'baumstruktur', 'sitzungen', 'bausteine'],
    estimatedMinutes: 4,
    relatedArticles: ['klient-anlegen', 'sitzung-planen', 'methoden-nutzen'],
    sections: [
      {
        heading: 'Was ist ein Fall?',
        paragraphs: [
          'Ein Fall (auch Coaching-Prozess) bündelt alle Sitzungen und Bausteine, die zu einem bestimmten Coaching-Thema gehören. Jeder Klient kann mehrere Fälle haben, z.B. "Führungskräfte-Coaching 2026" und "Karriere-Beratung".',
        ],
      },
      {
        heading: 'Fall anlegen',
        paragraphs: [],
        steps: [
          'Öffnen Sie das Klientenprofil',
          'Wechseln Sie zum Tab "Prozessmappe"',
          'Klicken Sie auf "+ Neuer Fall"',
          'Geben Sie einen Titel und optional eine Beschreibung ein',
          'Der Fall erscheint in der Baumstruktur: Klient → Fälle → Sitzungen → Bausteine',
        ],
      },
      {
        heading: 'Sitzungen und Bausteine',
        paragraphs: [
          'Innerhalb eines Falls erstellen Sie Sitzungen. Jede Sitzung kann mehrere Bausteine enthalten: Notizen, Methoden-Ergebnisse, Aufgaben und Dokumente.',
          'Alle Bausteine sind versioniert -- Sie können Änderungen nachverfolgen und bei Bedarf PDF-Downloads erstellen.',
        ],
        tip: 'Nutzen Sie die Baumstruktur in der Prozessmappe, um einen Überblick über den gesamten Coaching-Verlauf zu behalten.',
      },
      {
        heading: 'Fall abschließen',
        paragraphs: [
          'Wenn ein Coaching-Prozess beendet ist, können Sie den Fall als "Abgeschlossen" markieren. Abgeschlossene Fälle bleiben erhalten, werden aber nicht mehr im aktiven Bereich angezeigt.',
        ],
      },
    ],
  },

  // --- 25. Dokumente und Verträge ---
  {
    id: 'dokumente-vertraege',
    title: 'Dokumente und Verträge verwalten',
    shortTitle: 'Dokumente',
    description: 'Coaching-Verträge, Formulare und andere Dokumente hochladen und organisieren.',
    category: 'klienten',
    icon: 'FileText',
    relatedRoute: '/dokumente',
    keywords: ['dokument', 'dokumente', 'vertrag', 'verträge', 'upload', 'hochladen', 'datei', 'dateien', 'pdf', 'formular'],
    estimatedMinutes: 3,
    relatedArticles: ['klient-anlegen', 'cloud-speicher', 'dsgvo-export'],
    sections: [
      {
        heading: 'Dokumente hochladen',
        paragraphs: ['Unter "Dokumente" können Sie Verträge, Formulare und sonstige Dateien verwalten.'],
        steps: [
          'Navigieren Sie zu "Dokumente" in der Seitenleiste (oder direkt im Klientenprofil)',
          'Klicken Sie auf "Hochladen"',
          'Wählen Sie die Datei aus (PDF, Word, Bilder)',
          'Ordnen Sie das Dokument optional einem Klienten zu',
        ],
      },
      {
        heading: 'Verträge',
        paragraphs: [
          'Coaching-Verträge können Sie als PDF hochladen und dem Klienten zuordnen. Im Klientenprofil unter dem Tab "Dokumente" sind alle zugehörigen Dateien aufgelistet.',
        ],
        tip: 'Wenn Sie Google Drive verbunden haben, werden Dokumente automatisch in Ihrer Cloud gesichert.',
      },
    ],
  },

  // --- 26. Abo-Management ---
  {
    id: 'abo-management',
    title: 'Abo-Verwaltung: Pläne, Upgrade & Kündigung',
    shortTitle: 'Abo-Verwaltung',
    description: 'Alles zu den Abo-Plänen, Upgrade/Downgrade und Rechnungen für Ihr CoachDesk365-Abo.',
    category: 'einstellungen',
    icon: 'CreditCard',
    relatedRoute: '/mein-konto',
    keywords: ['abo', 'abonnement', 'plan', 'upgrade', 'downgrade', 'kündigung', 'kündigen', 'tarif', 'preis', 'kosten', 'basic', 'professional', 'enterprise', 'free', 'trial'],
    estimatedMinutes: 4,
    relatedArticles: ['account-einstellungen', 'team-management'],
    sections: [
      {
        heading: 'Verfügbare Pläne',
        paragraphs: [
          'CoachDesk365 bietet 4 Abo-Pläne: Free (14 Tage testen), Basic (49 €/Monat, 1 Coach), Professional S (99 €/Monat, bis 5 Coaches), Professional M (179 €/Monat, bis 15 Coaches) und Enterprise (auf Anfrage).',
          'Im Free Trial haben Sie Zugriff auf alle Funktionen. Nach Ablauf wechseln Sie in den gewählten Plan.',
        ],
      },
      {
        heading: 'Upgrade oder Downgrade',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Mein Konto"',
          'Im Bereich "Abonnement" sehen Sie Ihren aktuellen Plan',
          'Klicken Sie auf "Plan ändern"',
          'Wählen Sie den gewünschten Plan und bestätigen Sie',
        ],
        tip: 'Beim Upgrade wird der Preisunterschied anteilig berechnet. Beim Downgrade gelten die neuen Konditionen ab der nächsten Abrechnungsperiode.',
      },
      {
        heading: 'Kündigung',
        paragraphs: [
          'Sie können Ihr Abo jederzeit unter "Mein Konto" kündigen. Ihre Daten bleiben 90 Tage gespeichert und können in dieser Zeit exportiert werden (DSGVO-konform).',
        ],
      },
    ],
  },

  // --- 27. Buchungssystem ---
  {
    id: 'buchungssystem',
    title: 'Buchungssystem: Online-Termine für Klienten',
    shortTitle: 'Buchungen',
    description: 'Richten Sie Ihre öffentliche Buchungsseite ein und lassen Sie Klienten Termine selbst buchen.',
    category: 'termine',
    icon: 'CalendarPlus',
    relatedRoute: '/kalender',
    keywords: ['buchung', 'buchungen', 'buchen', 'online-termine', 'buchungsseite', 'buchungslink', 'slot', 'slots', 'verfügbarkeit', 'öffentlich'],
    estimatedMinutes: 4,
    relatedArticles: ['verfuegbarkeiten', 'sitzung-planen'],
    sections: [
      {
        heading: 'Ihre öffentliche Buchungsseite',
        paragraphs: [
          'Jeder Coach erhält eine persönliche Buchungsseite unter coachdesk365.com/buchen/ihr-name. Klienten können dort verfügbare Termine einsehen und direkt buchen.',
        ],
      },
      {
        heading: 'Buchungsseite einrichten',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Kalender" → Tab "Buchungen"',
          'Definieren Sie Ihre Verfügbarkeiten (Wochentage, Uhrzeiten)',
          'Passen Sie die Slot-Dauer an (30, 60 oder 90 Minuten)',
          'Teilen Sie den Buchungslink mit Ihren Klienten',
        ],
        tip: 'Der Buchungslink kann auch auf Ihrer Website eingebettet oder per E-Mail versendet werden.',
      },
      {
        heading: 'Buchungen verwalten',
        paragraphs: [
          'Eingegangene Buchungen erscheinen automatisch im Kalender und unter "Buchungen". Sie erhalten eine E-Mail-Benachrichtigung bei neuen Buchungen.',
          'Klienten erhalten 24 Stunden vor dem Termin eine automatische Erinnerung per E-Mail.',
        ],
      },
    ],
  },

  // --- 28. Klienten-Export ---
  {
    id: 'klienten-export',
    title: 'Klienten exportieren (CSV & PDF)',
    shortTitle: 'Klienten-Export',
    description: 'Exportieren Sie Ihre Klientenliste als CSV oder PDF für Ihre Buchhaltung oder Dokumentation.',
    category: 'klienten',
    icon: 'Download',
    relatedRoute: '/klienten',
    keywords: ['export', 'csv', 'pdf', 'klienten exportieren', 'download', 'liste', 'übersicht', 'buchhaltung'],
    estimatedMinutes: 2,
    relatedArticles: ['klient-anlegen', 'datev-export', 'dsgvo-export'],
    sections: [
      {
        heading: 'Export starten',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Klienten"',
          'Klicken Sie rechts oben auf "Exportieren"',
          'Wählen Sie das Format: CSV (für Excel/Buchhaltung) oder PDF (für Ausdruck)',
          'Die Datei wird automatisch heruntergeladen',
        ],
      },
      {
        heading: 'Was wird exportiert?',
        paragraphs: [
          'Der Export enthält: Name, E-Mail, Telefon, Adresse, Beruf, Status, Anzahl Fälle, Anzahl Sitzungen, Rechnungssumme und Anlagedatum.',
          'CSV-Dateien verwenden Semikolon (;) als Trennzeichen und sind direkt in Excel importierbar.',
        ],
        tip: 'Im Team-Modus (Professional S/M) enthält der Export alle Klienten der Praxis, nicht nur Ihre eigenen.',
      },
    ],
  },

  // --- 29. Termin-Erinnerungen ---
  {
    id: 'termin-erinnerungen',
    title: 'Automatische Termin-Erinnerungen',
    shortTitle: 'Erinnerungen',
    description: 'CoachDesk365 erinnert Ihre Klienten automatisch per E-Mail an bevorstehende Termine.',
    category: 'termine',
    icon: 'Clock',
    relatedRoute: '/kalender',
    keywords: ['erinnerung', 'erinnerungen', 'reminder', 'termin', 'benachrichtigung', 'automatisch', '24 stunden'],
    estimatedMinutes: 2,
    relatedArticles: ['sitzung-planen', 'buchungssystem', 'verfuegbarkeiten'],
    sections: [
      {
        heading: 'So funktionieren Erinnerungen',
        paragraphs: [
          'CoachDesk365 versendet automatisch 24 Stunden vor jedem Termin eine E-Mail-Erinnerung an den Klienten. Der Coach erhält ebenfalls eine Zusammenfassung der anstehenden Termine.',
          'Erinnerungen werden nur für Termine versendet, bei denen eine E-Mail-Adresse des Klienten hinterlegt ist.',
        ],
        tip: 'Stellen Sie sicher, dass bei Ihren Klienten eine gültige E-Mail-Adresse eingetragen ist, damit Erinnerungen ankommen.',
      },
    ],
  },

  // --- 30. Themes & Design ---
  {
    id: 'themes-design',
    title: 'Themes: Ihr persönliches Design',
    shortTitle: 'Themes',
    description: 'Wählen Sie aus 6 Farbthemes das passende Design für Ihre CoachDesk365-Oberfläche.',
    category: 'einstellungen',
    icon: 'Settings',
    relatedRoute: '/einstellungen',
    keywords: ['theme', 'themes', 'design', 'farbe', 'farben', 'farbschema', 'dark mode', 'dunkel', 'hell', 'desert rose', 'prestige navy', 'teal', 'purple'],
    estimatedMinutes: 2,
    relatedArticles: ['account-einstellungen'],
    sections: [
      {
        heading: 'Verfügbare Themes',
        paragraphs: [
          'CoachDesk365 bietet 6 Farbthemes: Boho Earth (Standard, warme Erdtöne), Golden Dusk (goldener Sonnenuntergang), Rose Quartz (sanftes Rosé), Prestige Navy (eleganter Dark Mode), Sage Forest (frisches Waldgrün) und Indigo Bloom (kräftiges Indigo).',
        ],
      },
      {
        heading: 'Theme ändern',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Mein Konto"',
          'Im Bereich "Darstellung" sehen Sie die verfügbaren Themes',
          'Klicken Sie auf das gewünschte Theme -- die Änderung wird sofort übernommen',
        ],
        tip: 'Ihr gewähltes Theme wird auch in PDF-Rechnungen und im Klienten-Portal verwendet.',
      },
    ],
  },

  // --- 31. White Label / Branding ---
  {
    id: 'white-label',
    title: 'White Label: Ihr Logo auf Rechnungen & Portal',
    shortTitle: 'White Label',
    description: 'Personalisieren Sie Rechnungen, Angebote und das Klienten-Portal mit Ihrem eigenen Logo und Firmennamen.',
    category: 'einstellungen',
    icon: 'Shield',
    relatedRoute: '/mein-konto',
    keywords: ['white label', 'branding', 'logo', 'firmenlogo', 'eigenes logo', 'personalisierung', 'marke', 'markenidentität'],
    estimatedMinutes: 3,
    relatedArticles: ['account-einstellungen', 'rechnung-erstellen', 'klienten-portal-komplett'],
    sections: [
      {
        heading: 'Ihr Logo einrichten',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Mein Konto" → "Profil"',
          'Laden Sie Ihr Logo hoch (PNG oder JPG, empfohlen: mindestens 200x200 Pixel)',
          'Das Logo erscheint automatisch auf Rechnungen, Angeboten und im Klienten-Portal',
        ],
      },
      {
        heading: 'Wo wird Ihr Branding angezeigt?',
        paragraphs: [
          'Rechnungs-PDFs: Ihr Logo erscheint im Header rechts neben Ihren Geschäftsdaten. Angebots-PDFs: Gleiches Layout wie Rechnungen. Klienten-Portal: Ihr Firmenname wird statt "CoachDesk365" angezeigt. Öffentliche Buchungsseite: Zeigt Ihren Firmennamen.',
        ],
      },
      {
        heading: 'Signatur auf PDFs',
        paragraphs: [
          'Zusätzlich können Sie eine persönliche Signatur hinterlegen, die auf allen PDF-Dokumenten nach den Zahlungsbedingungen erscheint.',
        ],
        tip: 'Nutzen Sie ein Logo mit transparentem Hintergrund für das beste Ergebnis auf Rechnungen.',
      },
    ],
  },

  // --- 32. Berichte & Statistiken ---
  {
    id: 'berichte-statistiken',
    title: 'Berichte & Statistiken: Ihr Coaching-Überblick',
    shortTitle: 'Berichte',
    description: 'Detaillierte Auswertungen zu Umsatz, Sitzungen, Klienten und Auslastung.',
    category: 'erste-schritte',
    icon: 'BarChart3',
    relatedRoute: '/berichte',
    keywords: ['bericht', 'berichte', 'statistik', 'statistiken', 'auswertung', 'report', 'reports', 'umsatz', 'auslastung', 'kennzahlen', 'kpi'],
    estimatedMinutes: 4,
    relatedArticles: ['dashboard-berichte', 'datev-export', 'klienten-export'],
    sections: [
      {
        heading: 'Berichtsübersicht',
        paragraphs: [
          'Unter "Berichte" finden Sie detaillierte Auswertungen zu allen wichtigen Kennzahlen Ihrer Coaching-Praxis. Die Daten werden automatisch aus Ihren Sitzungen, Rechnungen und Buchungen berechnet.',
        ],
      },
      {
        heading: 'Verfügbare Berichte',
        paragraphs: [
          'Umsatzbericht: Monatlicher und jährlicher Umsatz, aufgeschlüsselt nach Klienten. Sitzungsbericht: Anzahl, Dauer und Verteilung Ihrer Coaching-Sitzungen. Klientenbericht: Aktive/pausierte/abgeschlossene Klienten im Zeitverlauf. Auslastung: Wie viel Ihrer verfügbaren Zeit ist gebucht?',
        ],
      },
      {
        heading: 'Praxis-Statistiken (Team)',
        paragraphs: [
          'Im Team-Modus (Professional S/M) sehen Praxis-Owner und Admins zusätzlich die Praxis-Statistiken: Gesamtumsatz aller Coaches, Klientenverteilung pro Coach und Team-Auslastung.',
        ],
        tip: 'Berichte können als PDF exportiert werden -- ideal für Jahresgespräche oder die Steuerberatung.',
      },
    ],
  },

  // --- 33. Support-Tickets ---
  {
    id: 'support-tickets',
    title: 'Support-Tickets: Hilfe erhalten',
    shortTitle: 'Support-Tickets',
    description: 'So erstellen Sie Support-Anfragen und verfolgen den Bearbeitungsstatus.',
    category: 'einstellungen',
    icon: 'AlertTriangle',
    relatedRoute: '/support',
    keywords: ['support', 'ticket', 'tickets', 'hilfe', 'problem', 'fehler', 'anfrage', 'kontakt'],
    estimatedMinutes: 2,
    relatedArticles: ['schnellstart', 'klienten-portal-komplett'],
    sections: [
      {
        heading: 'Ticket erstellen',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Support" in der Seitenleiste',
          'Klicken Sie auf "+ Neues Ticket"',
          'Wählen Sie eine Kategorie und Priorität',
          'Beschreiben Sie Ihr Anliegen und senden Sie das Ticket ab',
        ],
      },
      {
        heading: 'Ticket-Status',
        paragraphs: [
          'Ihre Tickets durchlaufen die Status: Offen → In Bearbeitung → Beantwortet → Geschlossen. Sie werden per E-Mail benachrichtigt, sobald eine Antwort vorliegt.',
          'Auch Ihre Klienten können über das Klienten-Portal Support-Tickets erstellen -- diese erscheinen unter dem Tab "Klienten-Tickets".',
        ],
      },
    ],
  },

  // --- 34. Sitzung anlegen (Schritt für Schritt) ---
  {
    id: 'sitzung-anlegen',
    title: 'Neue Sitzung anlegen: Schritt für Schritt',
    shortTitle: 'Sitzung anlegen',
    description: 'Erstellen Sie eine Coaching-Sitzung direkt im Klientenprofil mit allen Details.',
    category: 'termine',
    icon: 'CalendarPlus',
    relatedRoute: '/klienten',
    keywords: ['sitzung', 'anlegen', 'erstellen', 'neue sitzung', 'session', 'coaching-sitzung', 'termin erstellen'],
    estimatedMinutes: 3,
    relatedArticles: ['sitzung-planen', 'faelle-verwalten', 'sitzung-notizen-dokumentieren'],
    sections: [
      {
        heading: 'Sitzung im Klientenprofil anlegen',
        paragraphs: ['Eine neue Sitzung wird immer innerhalb eines Falls erstellt. Öffnen Sie dazu den Klienten und navigieren Sie zum Prozess-Tab.'],
        steps: [
          'Öffnen Sie die Klientendetailseite',
          'Wechseln Sie zum Tab "Prozess"',
          'Falls noch kein Fall existiert, legen Sie zuerst einen Fall an',
          'Innerhalb des Falls klicken Sie auf "Neue Sitzung"',
          'Wählen Sie Datum, Uhrzeit, Dauer und Format (Online/Vor Ort/Telefon)',
          'Klicken Sie auf "Speichern"',
        ],
      },
      {
        heading: 'Sitzungstypen',
        paragraphs: [
          'CoachDesk365 unterstützt verschiedene Sitzungstypen: Einzelcoaching, Paarcoaching und Gruppencoaching. Der Typ bestimmt die Dokumentationsstruktur und wird auf der Rechnung angezeigt.',
        ],
      },
      {
        heading: 'Sitzung mit Kalender verknüpfen',
        paragraphs: [
          'Jede angelegte Sitzung erscheint automatisch in Ihrem Kalender. Sie können auch einen ICS-Export erstellen, um den Termin in Ihren externen Kalender zu übernehmen.',
        ],
        tip: 'Nutzen Sie die Quick-Action "Neue Sitzung" im Dashboard für schnellen Zugriff.',
      },
    ],
  },

  // --- 35. Prozessbausteine ---
  {
    id: 'prozessbausteine',
    title: 'Prozessbausteine: Sitzungen strukturieren',
    shortTitle: 'Prozessbausteine',
    description: 'Nutzen Sie Prozessbausteine wie Erstgespräch, Follow-Up und Coaching-Vertrag zur Strukturierung Ihrer Sitzungen.',
    category: 'klienten',
    icon: 'Layers',
    relatedRoute: '/klienten',
    keywords: ['prozessbausteine', 'bausteine', 'erstgespräch', 'follow-up', 'coaching-vertrag', 'vertrag', 'signatur', 'struktur', 'sitzungsblock'],
    estimatedMinutes: 5,
    relatedArticles: ['sitzung-anlegen', 'faelle-verwalten', 'sitzung-notizen-dokumentieren'],
    sections: [
      {
        heading: 'Was sind Prozessbausteine?',
        paragraphs: [
          'Prozessbausteine sind strukturierte Elemente innerhalb einer Sitzung. Sie ermöglichen es, den Coaching-Prozess professionell zu dokumentieren und nachvollziehbar zu gestalten.',
          'Jeder Baustein hat einen eigenen Typ, Status und kann als PDF exportiert oder per E-Mail versendet werden.',
        ],
      },
      {
        heading: 'Verfügbare Bausteintypen',
        paragraphs: [],
        steps: [
          'Erstgespräch: Strukturierter Rahmen für das erste Kennenlerngespräch',
          'Coaching-Vertrag: Vertrag mit digitaler Signatur direkt im System',
          'Sitzungsprotokoll: Dokumentation der einzelnen Sitzung',
          'Follow-Up: Nachbereitung und Aufgaben für den Klienten',
          'Abschlussgespräch: Zusammenfassung und Evaluation des Coaching-Prozesses',
        ],
      },
      {
        heading: 'Baustein hinzufügen',
        paragraphs: [],
        steps: [
          'Öffnen Sie eine Sitzung im Prozess-Tab des Klienten',
          'Klicken Sie auf "Baustein hinzufügen"',
          'Wählen Sie den gewünschten Typ',
          'Füllen Sie die Inhalte aus',
          'Speichern Sie den Baustein (Status: Entwurf)',
        ],
        tip: 'Bausteine können versioniert werden. Jede Änderung erstellt eine neue Version, ältere Versionen bleiben erhalten.',
      },
      {
        heading: 'PDF-Export und Versand',
        paragraphs: [
          'Jeden Baustein können Sie als PDF herunterladen oder direkt per E-Mail an den Klienten senden. Der Sende-Status wird angezeigt (Entwurf → Versendet → Gelesen).',
        ],
      },
    ],
  },

  // --- 36. Coaching-Pakete ---
  {
    id: 'coaching-pakete',
    title: 'Coaching-Pakete & Kontingente',
    shortTitle: 'Coaching-Pakete',
    description: 'Erstellen Sie Coaching-Pakete mit festem Stundenkontingent und verwalten Sie die Abrechnung.',
    category: 'finanzen',
    icon: 'Package',
    relatedRoute: '/klienten',
    keywords: ['paket', 'pakete', 'coaching-paket', 'kontingent', 'stunden', 'stundenkontingent', 'abrechnung', 'flatrate', 'bundle'],
    estimatedMinutes: 4,
    relatedArticles: ['rechnung-erstellen', 'angebote-verwalten', 'sitzung-anlegen'],
    sections: [
      {
        heading: 'Paket erstellen',
        paragraphs: ['Coaching-Pakete ermöglichen es, einem Klienten ein festes Kontingent an Sitzungen zuzuweisen und gebündelt abzurechnen.'],
        steps: [
          'Öffnen Sie die Klientendetailseite',
          'Wechseln Sie zum Tab "Pakete"',
          'Klicken Sie auf "Neues Paket"',
          'Definieren Sie: Name, Anzahl Sitzungen, Gesamtpreis',
          'Speichern Sie das Paket',
        ],
      },
      {
        heading: 'Kontingent verwalten',
        paragraphs: [
          'Sobald ein Paket aktiv ist, werden durchgeführte Sitzungen automatisch gegen das Kontingent verrechnet. Sie sehen jederzeit, wie viele Sitzungen verbraucht und wie viele noch übrig sind.',
        ],
        tip: 'Wenn das Kontingent aufgebraucht ist, erhalten Sie eine Benachrichtigung. Sie können das Paket dann verlängern oder ein neues erstellen.',
      },
      {
        heading: 'Paket abrechnen',
        paragraphs: [
          'Über den Button "Paket abrechnen" erstellen Sie eine Rechnung für das gesamte Paket. Alternativ können Sie Einzelsitzungen aus dem Paket separat abrechnen.',
        ],
      },
    ],
  },

  // --- 37. Zoom verbinden ---
  {
    id: 'zoom-verbinden',
    title: 'Zoom-Integration einrichten',
    shortTitle: 'Zoom verbinden',
    description: 'Verbinden Sie Ihr Zoom-Konto, um Meeting-Links automatisch in Buchungen einzufügen.',
    category: 'integrationen',
    icon: 'Video',
    relatedRoute: '/integrationen',
    keywords: ['zoom', 'video', 'meeting', 'online', 'integration', 'verbinden', 'videokonferenz', 'meeting-link'],
    estimatedMinutes: 3,
    relatedArticles: ['integrationen-uebersicht', 'buchungssystem', 'sitzung-planen'],
    sections: [
      {
        heading: 'Zoom-Konto verbinden',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Integrationen" in der Seitenleiste',
          'Suchen Sie den Abschnitt "Zoom"',
          'Geben Sie Ihre Zoom API-Zugangsdaten ein (Account-ID, Client-ID, Client-Secret)',
          'Klicken Sie auf "Verbinden"',
          'Bei Erfolg erscheint der Status "Verbunden"',
        ],
      },
      {
        heading: 'Zoom API-Zugangsdaten finden',
        paragraphs: [
          'Die Zugangsdaten finden Sie in Ihrem Zoom-Konto unter marketplace.zoom.us. Erstellen Sie dort eine Server-to-Server OAuth App. Die benötigten Scopes sind: meeting:write:admin und user:read:admin.',
        ],
        tip: 'Sie benötigen einen Zoom Pro-Account oder höher, um die API nutzen zu können.',
      },
      {
        heading: 'Automatische Meeting-Links',
        paragraphs: [
          'Nach der Verbindung wird bei jeder Online-Buchung automatisch ein Zoom-Meeting erstellt. Der Meeting-Link wird in die Buchungsbestätigung eingefügt und ist für den Klienten sichtbar.',
        ],
      },
    ],
  },

  // --- 38. Geschäftsdaten einrichten ---
  {
    id: 'geschaeftsdaten',
    title: 'Geschäftsdaten & Steuernummer einrichten',
    shortTitle: 'Geschäftsdaten',
    description: 'Hinterlegen Sie Ihre Geschäftsdaten für professionelle Rechnungen und rechtskonforme Dokumente.',
    category: 'einstellungen',
    icon: 'Building',
    relatedRoute: '/mein-konto',
    keywords: ['geschäftsdaten', 'steuernummer', 'ust-id', 'firma', 'firmenname', 'adresse', 'bankverbindung', 'iban', 'geschäft', 'unternehmen'],
    estimatedMinutes: 3,
    relatedArticles: ['account-einstellungen', 'rechnung-erstellen', 'white-label'],
    sections: [
      {
        heading: 'Geschäftsdaten eingeben',
        paragraphs: ['Ihre Geschäftsdaten erscheinen auf allen Rechnungen, Angeboten und im Klienten-Portal. Vollständige Daten sind Voraussetzung für rechtskonforme Rechnungen.'],
        steps: [
          'Navigieren Sie zu "Mein Konto"',
          'Im Abschnitt "Geschäftsdaten" tragen Sie ein:',
          'Firmenname / Praxisname',
          'Vollständige Geschäftsadresse',
          'Steuernummer oder USt-IdNr.',
          'Bankverbindung (IBAN, BIC, Bank)',
          'Speichern Sie Ihre Eingaben',
        ],
      },
      {
        heading: 'Wo erscheinen die Daten?',
        paragraphs: [
          'Rechnungs-PDFs: Absenderadresse, Steuernummer und Bankverbindung. Angebots-PDFs: Absenderadresse und Kontaktdaten. Klienten-Portal: Firmenname im Header. Buchungsseite: Firmenname in der Beschreibung.',
        ],
        tip: 'Überprüfen Sie Ihre Geschäftsdaten regelmäßig, besonders nach einem Umzug oder einer Rechtsformänderung.',
      },
    ],
  },

  // --- 39. Rechnungen & Steuern ---
  {
    id: 'rechnungen-steuern',
    title: 'Rechnungen & Steuern: GoBD-konform arbeiten',
    shortTitle: 'Rechnungen & Steuern',
    description: 'Erfahren Sie, wie CoachDesk365 GoBD-konforme ZUGFeRD-Rechnungen erstellt und den DATEV-Export unterstützt.',
    category: 'finanzen',
    icon: 'FileText',
    relatedRoute: '/rechnungen',
    keywords: ['rechnung', 'steuer', 'steuern', 'gobd', 'zugferd', 'datev', 'umsatzsteuer', 'mehrwertsteuer', 'finanzamt', 'buchhalter', 'steuerberater'],
    estimatedMinutes: 4,
    relatedArticles: ['rechnung-erstellen', 'datev-export', 'geschaeftsdaten'],
    sections: [
      {
        heading: 'ZUGFeRD-Rechnungen',
        paragraphs: [
          'CoachDesk365 erstellt automatisch ZUGFeRD-konforme PDF-Rechnungen. ZUGFeRD ist ein Standard für elektronische Rechnungen, der sowohl ein menschenlesbares PDF als auch eine maschinenlesbare XML-Datei enthält.',
          'Ihre Rechnungen entsprechen damit den Anforderungen des Finanzamts und können direkt in Buchhaltungssoftware importiert werden.',
        ],
      },
      {
        heading: 'Umsatzsteuer konfigurieren',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Mein Konto" → "Geschäftsdaten"',
          'Tragen Sie Ihre Steuernummer oder USt-IdNr. ein',
          'Wählen Sie Ihren Standard-Steuersatz (0%, 7% oder 19%)',
          'Der Steuersatz kann pro Rechnung individuell angepasst werden',
        ],
        tip: 'Als Kleinunternehmer nach §19 UStG wählen Sie 0% und aktivieren den Kleinunternehmer-Hinweis in den Einstellungen.',
      },
      {
        heading: 'DATEV-Export',
        paragraphs: [
          'Unter "Rechnungen" → "Exportieren" können Sie alle Rechnungen eines Zeitraums als DATEV-kompatible CSV-Datei exportieren. Die Datei kann direkt an Ihren Steuerberater weitergegeben werden.',
        ],
      },
    ],
  },

  // --- 40. Angebote verwalten ---
  {
    id: 'angebote-verwalten',
    title: 'Angebote erstellen und verwalten',
    shortTitle: 'Angebote verwalten',
    description: 'Erstellen Sie professionelle Angebote, versenden Sie diese per E-Mail und konvertieren Sie sie zu Rechnungen.',
    category: 'finanzen',
    icon: 'FileText',
    relatedRoute: '/angebote',
    keywords: ['angebot', 'angebote', 'erstellen', 'verwalten', 'kostenvoranschlag', 'offerte', 'konvertieren', 'versenden'],
    estimatedMinutes: 4,
    relatedArticles: ['angebot-erstellen', 'rechnung-erstellen', 'coaching-pakete'],
    sections: [
      {
        heading: 'Neues Angebot erstellen',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Angebote" (über Rechnungen → Tab "Angebote")',
          'Klicken Sie auf "+ Neues Angebot"',
          'Wählen Sie den Klienten aus',
          'Fügen Sie Positionen hinzu (Beschreibung, Menge, Einzelpreis)',
          'Ergänzen Sie optional Gültigkeitsdauer und Anmerkungen',
          'Speichern Sie das Angebot',
        ],
      },
      {
        heading: 'Angebot versenden',
        paragraphs: [
          'Über den Button "Per E-Mail senden" versenden Sie das Angebot als PDF-Anhang direkt an den Klienten. Der Versand wird protokolliert.',
        ],
      },
      {
        heading: 'Angebot in Rechnung umwandeln',
        paragraphs: [
          'Wird ein Angebot angenommen, können Sie es mit einem Klick in eine Rechnung konvertieren. Alle Positionen, Beträge und Klientendaten werden automatisch übernommen.',
        ],
        tip: 'Konvertierte Angebote behalten eine Referenz zum Original-Angebot, sodass die Zuordnung immer nachvollziehbar bleibt.',
      },
    ],
  },

  // --- 41. Online-Zahlung (Stripe) ---
  {
    id: 'online-zahlung',
    title: 'Online-Zahlung für Klienten einrichten',
    shortTitle: 'Online-Zahlung',
    description: 'Ermöglichen Sie Ihren Klienten die bequeme Online-Zahlung von Rechnungen über das Klienten-Portal.',
    category: 'finanzen',
    icon: 'CreditCard',
    relatedRoute: '/portal',
    keywords: ['zahlung', 'online', 'bezahlen', 'stripe', 'kreditkarte', 'überweisung', 'portal', 'checkout'],
    estimatedMinutes: 3,
    relatedArticles: ['stripe-zahlungen', 'klienten-portal-komplett', 'rechnung-erstellen'],
    sections: [
      {
        heading: 'So funktioniert die Online-Zahlung',
        paragraphs: [
          'Klienten können offene Rechnungen direkt im Klienten-Portal online bezahlen. Die Zahlung wird über den Zahlungsdienstleister Stripe abgewickelt, der Kreditkarten und weitere Zahlungsmethoden unterstützt.',
        ],
      },
      {
        heading: 'Voraussetzungen',
        paragraphs: [],
        steps: [
          'Der Klient muss zum Klienten-Portal eingeladen sein',
          'Die Rechnung muss den Status "Versendet" oder "Überfällig" haben',
          'Im Portal erscheint neben offenen Rechnungen der Button "Online bezahlen"',
        ],
      },
      {
        heading: 'Nach der Zahlung',
        paragraphs: [
          'Nach erfolgreicher Zahlung wird der Rechnungsstatus automatisch auf "Bezahlt" gesetzt. Sie erhalten eine Benachrichtigung und der Klient erhält eine Zahlungsbestätigung per E-Mail.',
        ],
        tip: 'Testen Sie die Online-Zahlung vorab im Testmodus, bevor Sie sie für Klienten freischalten.',
      },
    ],
  },

  // --- 42. Portal-Einstellungen ---
  {
    id: 'portal-einstellungen',
    title: 'Klienten-Portal konfigurieren',
    shortTitle: 'Portal-Einstellungen',
    description: 'Richten Sie das Klienten-Portal ein und verwalten Sie Einladungen, Zugänge und sichtbare Funktionen.',
    category: 'klienten',
    icon: 'Globe',
    relatedRoute: '/klienten',
    keywords: ['portal', 'klienten-portal', 'einladung', 'einladen', 'zugang', 'konfigurieren', 'einrichten', 'setup'],
    estimatedMinutes: 4,
    relatedArticles: ['klienten-portal-komplett', 'klienten-portal-tabs', 'klient-anlegen'],
    sections: [
      {
        heading: 'Klienten zum Portal einladen',
        paragraphs: [],
        steps: [
          'Öffnen Sie die Klientendetailseite',
          'Wechseln Sie zum Tab "Portal"',
          'Klicken Sie auf "Zum Portal einladen"',
          'Der Klient erhält eine E-Mail mit einem Setup-Link',
          'Nach der Registrierung kann der Klient sich am Portal anmelden',
        ],
      },
      {
        heading: 'Was sieht der Klient im Portal?',
        paragraphs: [
          'Das Klienten-Portal bietet: Dashboard mit nächstem Termin, Termine buchen und einsehen, Rechnungen einsehen und bezahlen, Dokumente herunterladen, Änderungsanträge stellen, Support-Tickets erstellen und DSGVO-Rechte ausüben.',
        ],
      },
      {
        heading: 'Einladung erneut senden',
        paragraphs: [
          'Falls der Klient die Einladung verpasst hat, können Sie über den Button "Erneut einladen" eine neue E-Mail versenden. Der Setup-Link bleibt 7 Tage gültig.',
        ],
        tip: 'Der Klient sieht im Portal Ihren Firmennamen (statt "CoachDesk365"), wenn Sie unter Mein Konto einen Firmennamen hinterlegt haben.',
      },
    ],
  },

  // --- 43. Eigene Methoden erstellen ---
  {
    id: 'custom-methods',
    title: 'Eigene Coaching-Methoden erstellen',
    shortTitle: 'Eigene Methoden',
    description: 'Erstellen Sie individuelle Coaching-Methoden und Arbeitsblätter für Ihre Praxis.',
    category: 'methoden',
    icon: 'PenTool',
    relatedRoute: '/methoden',
    keywords: ['methode', 'eigene methode', 'erstellen', 'custom', 'individuell', 'arbeitsblatt', 'vorlage', 'eigene'],
    estimatedMinutes: 4,
    relatedArticles: ['methoden-nutzen', 'prozessbausteine'],
    sections: [
      {
        heading: 'Eigene Methode anlegen',
        paragraphs: ['Neben den 44 vorinstallierten Methoden können Sie auch eigene Coaching-Methoden erstellen, die genau zu Ihrer Arbeitsweise passen.'],
        steps: [
          'Navigieren Sie zu "Methoden"',
          'Klicken Sie auf "Eigene Methode erstellen"',
          'Geben Sie einen Titel und eine Beschreibung ein',
          'Wählen Sie eine Kategorie (oder erstellen Sie eine neue)',
          'Ergänzen Sie optional Anleitung, Dauer und Zielgruppe',
          'Speichern Sie die Methode',
        ],
      },
      {
        heading: 'Arbeitsblätter hinzufügen',
        paragraphs: [
          'Zu jeder Methode können Sie Arbeitsblätter als PDF hochladen. Diese stehen dann in der Sitzungsdokumentation zur Verfügung und können an Klienten versendet werden.',
        ],
      },
      {
        heading: 'Methoden in Sitzungen nutzen',
        paragraphs: [
          'Eigene Methoden erscheinen in der Methodenauswahl während der Sitzungsdokumentation, genau wie die vorinstallierten Methoden.',
        ],
        tip: 'Ihre eigenen Methoden sind nur für Sie sichtbar und werden nicht mit anderen Coaches geteilt.',
      },
    ],
  },

  // --- 44. Fälle & Baumstruktur ---
  {
    id: 'faelle-baumstruktur',
    title: 'Fälle, Sitzungen & Bausteine: Die Baumstruktur',
    shortTitle: 'Baumstruktur',
    description: 'Verstehen Sie die hierarchische Struktur: Klient → Fall → Sitzung → Baustein.',
    category: 'klienten',
    icon: 'GitBranch',
    relatedRoute: '/klienten',
    keywords: ['fall', 'fälle', 'baumstruktur', 'hierarchie', 'struktur', 'prozess', 'sitzung', 'baustein', 'tree'],
    estimatedMinutes: 3,
    relatedArticles: ['faelle-verwalten', 'prozessbausteine', 'sitzung-anlegen'],
    sections: [
      {
        heading: 'Die Prozessstruktur verstehen',
        paragraphs: [
          'CoachDesk365 organisiert den Coaching-Prozess in einer hierarchischen Baumstruktur. Diese Struktur sorgt dafür, dass alle Informationen logisch geordnet und leicht auffindbar sind.',
        ],
      },
      {
        heading: 'Die vier Ebenen',
        paragraphs: [],
        steps: [
          'Klient: Die oberste Ebene -- enthält Stammdaten, Kontaktdaten und alle zugehörigen Fälle',
          'Fall: Ein Coaching-Auftrag (z.B. "Führungskräfte-Entwicklung Q1 2026"). Ein Klient kann mehrere Fälle haben',
          'Sitzung: Ein einzelner Coaching-Termin innerhalb eines Falls',
          'Baustein: Strukturierte Inhalte innerhalb einer Sitzung (Erstgespräch, Protokoll, Follow-Up etc.)',
        ],
      },
      {
        heading: 'Navigation in der Baumstruktur',
        paragraphs: [
          'Im Prozess-Tab des Klienten sehen Sie die Baumstruktur als aufklappbare Liste. Klicken Sie auf einen Fall, um die Sitzungen zu sehen. Klicken Sie auf eine Sitzung, um die Bausteine anzuzeigen.',
        ],
        tip: 'Nutzen Sie die Baumstruktur, um den Coaching-Fortschritt auf einen Blick zu erfassen: Wie viele Sitzungen wurden durchgeführt? Welche Bausteine sind noch offen?',
      },
    ],
  },

  // --- 45. Klienten-Portal Tabs ---
  {
    id: 'klienten-portal-tabs',
    title: 'Das Klienten-Portal im Detail',
    shortTitle: 'Portal-Bereiche',
    description: 'Alle Bereiche des Klienten-Portals erklärt: Dashboard, Termine, Rechnungen, Dokumente und mehr.',
    category: 'klienten',
    icon: 'LayoutGrid',
    relatedRoute: '/portal',
    keywords: ['portal', 'tabs', 'bereiche', 'dashboard', 'termine', 'rechnungen', 'dokumente', 'klient', 'klienten-portal'],
    estimatedMinutes: 4,
    relatedArticles: ['klienten-portal-komplett', 'portal-einstellungen', 'online-zahlung'],
    sections: [
      {
        heading: 'Portal-Dashboard',
        paragraphs: [
          'Das Dashboard zeigt dem Klienten auf einen Blick: den nächsten anstehenden Termin, offene Rechnungen und aktuelle Nachrichten. Die Darstellung ist kompakt als 2-Spalten-Grid angelegt.',
        ],
      },
      {
        heading: 'Termine',
        paragraphs: [
          'Im Termine-Bereich sieht der Klient alle gebuchten und verfügbaren Termine. Eine Split-View zeigt links den Kalender und rechts die Details zum ausgewählten Termin. Neue Termine können direkt über die verfügbaren Slots gebucht werden.',
        ],
      },
      {
        heading: 'Rechnungen & Zahlung',
        paragraphs: [
          'Alle Rechnungen werden mit EUR-Betrag angezeigt. PDFs können inline als Vorschau betrachtet oder heruntergeladen werden. Offene Rechnungen bieten einen "Online bezahlen"-Button.',
        ],
      },
      {
        heading: 'Weitere Bereiche',
        paragraphs: [
          'Dokumente: Bereitgestellte Dokumente und Verträge einsehen und herunterladen. Änderungsanträge: Adress- oder Datenänderungen beantragen, die der Coach genehmigt. Support: Tickets erstellen und Antworten verfolgen. DSGVO: Datenauskunft anfordern oder Löschung beantragen.',
        ],
      },
    ],
  },

  // --- 46. Slot-Management ---
  {
    id: 'slot-management',
    title: 'Verfügbare Zeitslots verwalten',
    shortTitle: 'Slot-Management',
    description: 'Richten Sie Ihre verfügbaren Buchungsslots ein, damit Klienten online Termine buchen können.',
    category: 'termine',
    icon: 'Clock',
    relatedRoute: '/kalender',
    keywords: ['slot', 'slots', 'zeitslot', 'verfügbarkeit', 'buchbar', 'freie termine', 'buchungsslot', 'availability'],
    estimatedMinutes: 3,
    relatedArticles: ['verfuegbarkeiten', 'buchungssystem', 'sitzung-planen'],
    sections: [
      {
        heading: 'Zeitslots einrichten',
        paragraphs: ['Zeitslots definieren, wann Klienten über Ihre öffentliche Buchungsseite oder das Klienten-Portal Termine buchen können.'],
        steps: [
          'Navigieren Sie zu "Kalender" → Tab "Buchungen"',
          'Klicken Sie auf "Verfügbarkeit bearbeiten"',
          'Wählen Sie die Wochentage und Uhrzeiten, an denen Sie verfügbar sind',
          'Definieren Sie die Dauer pro Slot (z.B. 60 oder 90 Minuten)',
          'Legen Sie Pufferzeiten zwischen Terminen fest',
          'Speichern Sie Ihre Verfügbarkeit',
        ],
      },
      {
        heading: 'Einzelne Slots sperren',
        paragraphs: [
          'Wenn Sie an einem bestimmten Tag nicht verfügbar sind, können Sie einzelne Slots sperren, ohne Ihre generelle Verfügbarkeit zu ändern. Gesperrte Slots erscheinen nicht mehr auf der Buchungsseite.',
        ],
        tip: 'Bereits gebuchte Termine bleiben bestehen, auch wenn Sie die Verfügbarkeit nachträglich ändern.',
      },
    ],
  },

  // --- 47. Sitzungsnotizen dokumentieren ---
  {
    id: 'sitzung-notizen-dokumentieren',
    title: 'Sitzungsnotizen professionell dokumentieren',
    shortTitle: 'Sitzungsnotizen',
    description: 'Nutzen Sie die strukturierten Sitzungsblöcke für professionelle Coaching-Dokumentation.',
    category: 'termine',
    icon: 'FileEdit',
    relatedRoute: '/klienten',
    keywords: ['notizen', 'sitzungsnotizen', 'dokumentation', 'protokoll', 'dokumentieren', 'aufzeichnen', 'sitzungsblock', 'draft'],
    estimatedMinutes: 4,
    relatedArticles: ['sitzung-anlegen', 'prozessbausteine', 'faelle-verwalten'],
    sections: [
      {
        heading: 'Sitzungsblock-Struktur',
        paragraphs: [
          'Jeder Sitzungsblock bietet 15 strukturierte Sektionen für eine umfassende Dokumentation: von der Vorbereitung über Ziele und Interventionen bis hin zur Reflexion und den nächsten Schritten.',
        ],
      },
      {
        heading: 'Notizen erfassen',
        paragraphs: [],
        steps: [
          'Öffnen Sie den Sitzungsblock im Prozess-Tab des Klienten',
          'Füllen Sie die relevanten Sektionen aus',
          'Entwürfe werden automatisch als "Draft" gespeichert',
          'Wenn fertig, klicken Sie auf "Abschließen"',
        ],
        tip: 'Sie müssen nicht alle 15 Sektionen ausfüllen. Nutzen Sie nur die Bereiche, die für Ihre Sitzung relevant sind. Entwürfe gehen nicht verloren.',
      },
      {
        heading: 'Notizen exportieren',
        paragraphs: [
          'Fertige Sitzungsnotizen können als PDF exportiert und an den Klienten versendet werden. Beachten Sie: Im Klienten-Portal sind Ihre internen Notizen NICHT sichtbar -- der Klient sieht nur die für ihn freigegebenen Bausteine.',
        ],
      },
    ],
  },

  // --- 48. Vertragsvorlagen ---
  {
    id: 'vertragsvorlagen-anpassen',
    title: 'Vertragsvorlagen anpassen',
    shortTitle: 'Vertragsvorlagen',
    description: 'Erstellen und bearbeiten Sie Coaching-Verträge mit digitaler Signatur.',
    category: 'finanzen',
    icon: 'FileSignature',
    relatedRoute: '/dokumente',
    keywords: ['vertrag', 'vertragsvorlage', 'vorlage', 'signatur', 'unterschrift', 'digital', 'coaching-vertrag', 'kontrakt'],
    estimatedMinutes: 3,
    relatedArticles: ['dokumente-vertraege', 'prozessbausteine', 'klient-anlegen'],
    sections: [
      {
        heading: 'Vertrag als Prozessbaustein',
        paragraphs: [
          'In CoachDesk365 wird der Coaching-Vertrag als Prozessbaustein innerhalb einer Sitzung erstellt. So ist er direkt mit dem Klienten und dem Fall verknüpft.',
        ],
        steps: [
          'Öffnen Sie die erste Sitzung eines Falls',
          'Fügen Sie den Baustein "Coaching-Vertrag" hinzu',
          'Passen Sie die Vertragsdetails an (Umfang, Dauer, Kosten, Vertraulichkeit)',
          'Der Klient kann den Vertrag digital im Portal unterschreiben',
        ],
      },
      {
        heading: 'Digitale Signatur',
        paragraphs: [
          'Der Vertrag kann über das Klienten-Portal digital signiert werden. Der Klient zeichnet seine Unterschrift direkt im Browser. Die signierte Version wird automatisch gespeichert und als PDF exportiert.',
        ],
        tip: 'Erstellen Sie den Vertrag idealerweise vor oder direkt nach dem Erstgespräch, damit der Coaching-Prozess formal abgesichert ist.',
      },
    ],
  },

  // --- 49. Archivieren ---
  {
    id: 'archivieren',
    title: 'Klienten, Fälle & Nachrichten archivieren',
    shortTitle: 'Archivieren',
    description: 'Lernen Sie, wie Sie abgeschlossene Klienten, Fälle und Konversationen archivieren und wiederherstellen.',
    category: 'klienten',
    icon: 'Archive',
    relatedRoute: '/klienten',
    keywords: ['archiv', 'archivieren', 'abgeschlossen', 'löschen', 'inaktiv', 'wiederherstellen', 'aufräumen', 'archivierung'],
    estimatedMinutes: 3,
    relatedArticles: ['klient-anlegen', 'faelle-verwalten', 'nachrichten-messaging'],
    sections: [
      {
        heading: 'Klienten archivieren',
        paragraphs: [
          'Wenn ein Coaching-Prozess abgeschlossen ist, können Sie den Klienten archivieren. Archivierte Klienten werden aus der Hauptliste ausgeblendet, bleiben aber vollständig erhalten.',
        ],
        steps: [
          'Öffnen Sie die Klientendetailseite',
          'Klicken Sie auf das Menü (drei Punkte) rechts oben',
          'Wählen Sie "Archivieren"',
          'Bestätigen Sie die Archivierung',
        ],
        tip: 'Archivierte Klienten können jederzeit wiederhergestellt werden. Alle Daten (Fälle, Sitzungen, Rechnungen) bleiben erhalten.',
      },
      {
        heading: 'Fälle abschließen',
        paragraphs: [
          'Einzelne Fälle können über den Status auf "Abgeschlossen" gesetzt werden. Abgeschlossene Fälle werden in der Baumstruktur eingeklappt dargestellt, sind aber weiterhin einsehbar.',
        ],
      },
      {
        heading: 'Nachrichten archivieren',
        paragraphs: [
          'Im Messaging-Bereich können Sie Konversationen manuell archivieren. Archivierte Konversationen verschwinden aus dem Posteingang und sind über den Filter "Archiv" erreichbar. Zusätzlich werden inaktive Konversationen nach 90 Tagen automatisch archiviert.',
        ],
      },
      {
        heading: 'Archivierte Elemente anzeigen',
        paragraphs: [
          'Klienten: Nutzen Sie den Filter "Archiviert" in der Klientenliste. Nachrichten: Wählen Sie den Filter "Archiv" im Posteingang. Fälle: Abgeschlossene Fälle bleiben in der Prozessansicht sichtbar.',
        ],
      },
    ],
  },

  // --- 50. Notiz-Vorlagen ---
  {
    id: 'notiz-vorlagen',
    title: 'Notiz-Vorlagen für schnellere Dokumentation',
    shortTitle: 'Notiz-Vorlagen',
    description: 'Nutzen Sie vorgefertigte Notiz-Vorlagen, um Sitzungen schneller und einheitlicher zu dokumentieren.',
    category: 'methoden',
    icon: 'ClipboardList',
    relatedRoute: '/methoden',
    keywords: ['vorlage', 'vorlagen', 'notiz', 'notizen', 'template', 'dokumentation', 'schneller', 'standardisiert'],
    estimatedMinutes: 3,
    relatedArticles: ['sitzung-notizen-dokumentieren', 'prozessbausteine', 'custom-methods'],
    sections: [
      {
        heading: 'Vorlagen nutzen',
        paragraphs: [
          'CoachDesk365 bietet strukturierte Dokumentationsvorlagen für verschiedene Sitzungsarten. Die 15 Sektionen im Sitzungsblock dienen als Leitfaden, den Sie je nach Bedarf ausfüllen.',
        ],
      },
      {
        heading: 'Strukturierte Sektionen',
        paragraphs: [],
        steps: [
          'Vorbereitung: Was wurde vorab besprochen oder vorbereitet?',
          'Ziele: Was soll in dieser Sitzung erreicht werden?',
          'Themen & Anliegen: Worum geht es dem Klienten?',
          'Interventionen: Welche Methoden wurden eingesetzt?',
          'Erkenntnisse: Welche Aha-Momente gab es?',
          'Vereinbarungen: Was wurde beschlossen?',
          'Hausaufgaben: Was tut der Klient bis zur nächsten Sitzung?',
          'Reflexion: Wie bewerten Sie die Sitzung?',
        ],
      },
      {
        heading: 'Eigene Methoden als Vorlage',
        paragraphs: [
          'Über die Methoden-Bibliothek können Sie eigene Methoden mit Arbeitsblättern erstellen. Diese stehen dann als Vorlage in der Sitzungsdokumentation zur Verfügung.',
        ],
        tip: 'Nicht alle Sektionen müssen ausgefüllt werden. Konzentrieren Sie sich auf die Bereiche, die für Ihre Coaching-Praxis relevant sind.',
      },
    ],
  },

  // --- 51. E-Mail-Vorlagen einrichten ---
  {
    id: 'email-vorlagen',
    title: 'E-Mail-Vorlagen anpassen',
    shortTitle: 'E-Mail-Vorlagen',
    description: 'Passen Sie E-Mail-Vorlagen für Rechnungen, Erinnerungen und Benachrichtigungen an Ihren Stil an.',
    category: 'einstellungen',
    icon: 'Mail',
    relatedRoute: '/integrationen',
    keywords: ['email', 'e-mail', 'vorlage', 'vorlagen', 'template', 'anpassen', 'platzhalter', 'betreff', 'signatur'],
    estimatedMinutes: 3,
    relatedArticles: ['rechnung-erstellen', 'nachrichten-messaging', 'account-einstellungen'],
    sections: [
      {
        heading: 'Vorlagen bearbeiten',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Integrationen"',
          'Wechseln Sie zum Bereich "E-Mail-Vorlagen"',
          'Wählen Sie die Vorlage aus, die Sie anpassen möchten (z.B. Rechnungsversand, Terminbestätigung)',
          'Bearbeiten Sie Betreff und Text',
          'Nutzen Sie Platzhalter wie {{klient_name}}, {{termin_datum}}, {{betrag}}',
          'Speichern Sie die Vorlage',
        ],
      },
      {
        heading: 'Verfügbare Platzhalter',
        paragraphs: [
          'Platzhalter werden beim Versand automatisch durch die echten Daten ersetzt: {{klient_name}} (Name des Klienten), {{coach_name}} (Ihr Name), {{termin_datum}} (Datum des Termins), {{betrag}} (Rechnungsbetrag), {{firma}} (Ihr Firmenname).',
        ],
        tip: 'Testen Sie eine Vorlage, indem Sie sich selbst eine Test-E-Mail senden, bevor Sie sie an Klienten verwenden.',
      },
    ],
  },

  // --- 52. Google Drive Synchronisation ---
  {
    id: 'google-drive-sync',
    title: 'Google Drive: Automatische Synchronisation',
    shortTitle: 'Google Drive Sync',
    description: 'Richten Sie die automatische Synchronisation Ihrer Dokumente mit Google Drive ein.',
    category: 'integrationen',
    icon: 'Cloud',
    relatedRoute: '/integrationen',
    keywords: ['google', 'drive', 'sync', 'synchronisation', 'cloud', 'backup', 'speicher', 'automatisch', 'upload'],
    estimatedMinutes: 4,
    relatedArticles: ['cloud-speicher', 'integrationen-uebersicht', 'dokumente-vertraege'],
    sections: [
      {
        heading: 'Google Drive verbinden',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Integrationen" → "Cloud-Speicher"',
          'Klicken Sie auf "Google Drive verbinden"',
          'Melden Sie sich mit Ihrem Google-Konto an',
          'Erteilen Sie CoachDesk365 die nötigen Berechtigungen',
          'Nach der Verbindung erscheint der Status "Verbunden"',
        ],
      },
      {
        heading: 'Automatischer Upload',
        paragraphs: [
          'CoachDesk365 lädt automatisch Dateien in 5 Kategorien auf Google Drive hoch: Rechnungen, Angebote, Verträge, Dokumente und Sitzungsnotizen. Für jede Kategorie wird ein eigener Ordner angelegt.',
        ],
      },
      {
        heading: 'Ordnerstruktur',
        paragraphs: [
          'Die Ordnerstruktur wird automatisch erstellt: CoachDesk365 → Rechnungen, Angebote, Verträge, Dokumente, Notizen. Innerhalb jedes Ordners werden die Dateien nach Klientenname sortiert.',
        ],
        tip: 'Der Upload erfolgt automatisch im Hintergrund. Sie müssen keine Dateien manuell hochladen.',
      },
    ],
  },

  // --- 53. Outlook-Kalender Sync ---
  {
    id: 'outlook-kalender-sync',
    title: 'Outlook-Kalender synchronisieren',
    shortTitle: 'Outlook-Kalender',
    description: 'Verbinden Sie Ihren Outlook-Kalender, um Termine automatisch zu synchronisieren.',
    category: 'integrationen',
    icon: 'Calendar',
    relatedRoute: '/integrationen',
    keywords: ['outlook', 'kalender', 'sync', 'synchronisation', 'microsoft', 'office', '365', 'termin', 'calendar'],
    estimatedMinutes: 3,
    relatedArticles: ['integrationen-uebersicht', 'sitzung-planen', 'termin-erinnerungen'],
    sections: [
      {
        heading: 'Outlook verbinden',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Integrationen" → "Kalender"',
          'Klicken Sie auf "Outlook verbinden"',
          'Melden Sie sich mit Ihrem Microsoft-Konto an',
          'Erteilen Sie die Kalender-Berechtigung',
          'Wählen Sie den Kalender, der synchronisiert werden soll',
        ],
      },
      {
        heading: 'Was wird synchronisiert?',
        paragraphs: [
          'Alle in CoachDesk365 angelegten Termine erscheinen automatisch in Ihrem Outlook-Kalender. Änderungen (Verschiebung, Absage) werden in beide Richtungen synchronisiert.',
        ],
      },
      {
        heading: 'ICS-Feed als Alternative',
        paragraphs: [
          'Falls Sie keine direkte Verbindung wünschen, können Sie auch den ICS-Feed nutzen. Kopieren Sie die Feed-URL aus den Integrationseinstellungen und fügen Sie sie in Outlook als "Internetkalender" hinzu.',
        ],
        tip: 'Der ICS-Feed ist schreibgeschützt. Für volle Zwei-Wege-Synchronisation nutzen Sie die direkte Outlook-Verbindung.',
      },
    ],
  },

  // --- 54. Änderungsanträge verwalten ---
  {
    id: 'aenderungsantraege',
    title: 'Änderungsanträge von Klienten bearbeiten',
    shortTitle: 'Änderungsanträge',
    description: 'So bearbeiten und genehmigen Sie Änderungsanträge, die Klienten über das Portal stellen.',
    category: 'klienten',
    icon: 'ClipboardCheck',
    relatedRoute: '/support',
    keywords: ['änderungsantrag', 'änderung', 'antrag', 'genehmigen', 'ablehnen', 'klient', 'daten', 'adresse', 'portal'],
    estimatedMinutes: 3,
    relatedArticles: ['klienten-portal-komplett', 'support-tickets', 'portal-einstellungen'],
    sections: [
      {
        heading: 'Was sind Änderungsanträge?',
        paragraphs: [
          'Klienten können über das Portal Änderungen an ihren Daten beantragen (z.B. neue Adresse, Telefonnummer, E-Mail). Diese Anträge erscheinen bei Ihnen zur Prüfung und müssen von Ihnen genehmigt werden.',
        ],
      },
      {
        heading: 'Anträge bearbeiten',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Support" → Tab "Änderungsanträge"',
          'Sie sehen alle offenen Anträge mit dem gewünschten Änderungsdetail',
          'Klicken Sie auf "Genehmigen", um die Änderung zu übernehmen',
          'Oder klicken Sie auf "Ablehnen" mit einem optionalen Kommentar',
        ],
      },
      {
        heading: 'Benachrichtigung',
        paragraphs: [
          'Der Klient wird per E-Mail über die Entscheidung informiert. Genehmigte Änderungen werden automatisch in den Klientendaten übernommen.',
        ],
        tip: 'Änderungsanträge bieten eine DSGVO-konforme Dokumentation aller Datenänderungen durch Klienten.',
      },
    ],
  },

  // --- 55. 2FA & Sicherheit ---
  {
    id: 'security-2fa',
    title: 'Zwei-Faktor-Authentifizierung (2FA)',
    shortTitle: '2FA einrichten',
    description: 'Schützen Sie Ihr Konto mit einer zusätzlichen Sicherheitsstufe durch 2FA.',
    category: 'einstellungen',
    icon: 'ShieldCheck',
    relatedRoute: '/mein-konto',
    keywords: ['2fa', 'zwei-faktor', 'authentifizierung', 'sicherheit', 'totp', 'authenticator', 'backup-code', 'schutz', 'login'],
    estimatedMinutes: 3,
    relatedArticles: ['passwort-sicherheit', 'account-einstellungen'],
    sections: [
      {
        heading: '2FA aktivieren',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Mein Konto" → "Sicherheit"',
          'Klicken Sie auf "2FA aktivieren"',
          'Wählen Sie die Methode: Authenticator-App (TOTP) oder E-Mail-Code',
          'Bei TOTP: Scannen Sie den QR-Code mit einer Authenticator-App (z.B. Google Authenticator, Authy)',
          'Geben Sie den Bestätigungscode ein',
          'Speichern Sie Ihre Backup-Codes an einem sicheren Ort',
        ],
      },
      {
        heading: 'Backup-Codes',
        paragraphs: [
          'Bei der 2FA-Einrichtung erhalten Sie einmalige Backup-Codes. Diese können Sie verwenden, falls Sie keinen Zugriff auf Ihre Authenticator-App haben. Jeder Code kann nur einmal verwendet werden.',
        ],
        tip: 'Drucken Sie die Backup-Codes aus oder speichern Sie sie in einem Passwort-Manager. Ohne Backup-Codes und ohne Authenticator-App können Sie sich nicht mehr anmelden!',
      },
      {
        heading: '2FA deaktivieren',
        paragraphs: [
          'Unter "Mein Konto" → "Sicherheit" können Sie 2FA jederzeit deaktivieren. Zur Bestätigung müssen Sie Ihr aktuelles Passwort eingeben.',
        ],
      },
    ],
  },

  // --- 56. DSGVO & Löschanfragen ---
  {
    id: 'datenschutz-loeschanfragen',
    title: 'DSGVO: Löschanfragen & Datenauskunft',
    shortTitle: 'DSGVO-Rechte',
    description: 'So verarbeiten Sie DSGVO-Anfragen Ihrer Klienten: Datenauskunft, Löschung und Export.',
    category: 'einstellungen',
    icon: 'Shield',
    relatedRoute: '/mein-konto',
    keywords: ['dsgvo', 'datenschutz', 'löschung', 'löschen', 'datenauskunft', 'auskunft', 'recht', 'gdpr', 'privacy', 'anfrage'],
    estimatedMinutes: 4,
    relatedArticles: ['dsgvo-export', 'klienten-portal-komplett', 'account-einstellungen'],
    sections: [
      {
        heading: 'Klienten-Rechte nach DSGVO',
        paragraphs: [
          'Ihre Klienten haben das Recht auf Datenauskunft (Art. 15 DSGVO), Löschung (Art. 17) und Datenportabilität (Art. 20). CoachDesk365 unterstützt Sie bei der Umsetzung dieser Rechte.',
        ],
      },
      {
        heading: 'Datenauskunft erteilen',
        paragraphs: [],
        steps: [
          'Klienten können über das Portal eine Datenauskunft anfordern',
          'Sie erhalten eine Benachrichtigung',
          'Unter "Mein Konto" → "Datenschutz" sehen Sie alle Anfragen',
          'Klicken Sie auf "Export erstellen" -- alle Klientendaten werden als JSON/PDF exportiert',
          'Senden Sie den Export an den Klienten',
        ],
      },
      {
        heading: 'Löschanfrage bearbeiten',
        paragraphs: [
          'Bei einer Löschanfrage werden alle personenbezogenen Daten des Klienten unwiderruflich gelöscht: Stammdaten, Sitzungsnotizen, Dokumente und Nachrichten. Rechnungen bleiben aus steuerrechtlichen Gründen (Aufbewahrungspflicht 10 Jahre) erhalten, werden aber anonymisiert.',
        ],
        tip: 'Prüfen Sie vor der Löschung, ob noch offene Rechnungen oder laufende Fälle existieren. Die Löschung kann nicht rückgängig gemacht werden.',
      },
    ],
  },

  // --- 57. Klienten-Status ändern ---
  {
    id: 'klienten-status',
    title: 'Klienten-Status: Aktiv, Pausiert, Archiviert',
    shortTitle: 'Klienten-Status',
    description: 'Verwalten Sie den Status Ihrer Klienten und organisieren Sie Ihre aktive Klientenliste.',
    category: 'klienten',
    icon: 'UserCheck',
    relatedRoute: '/klienten',
    keywords: ['status', 'aktiv', 'pausiert', 'archiviert', 'inaktiv', 'abgeschlossen', 'klient', 'verwalten'],
    estimatedMinutes: 2,
    relatedArticles: ['klient-anlegen', 'archivieren', 'faelle-verwalten'],
    sections: [
      {
        heading: 'Verfügbare Status',
        paragraphs: [],
        steps: [
          'Aktiv: Klient ist in laufender Betreuung (Standard)',
          'Pausiert: Coaching ist vorübergehend unterbrochen',
          'Abgeschlossen: Coaching-Prozess ist beendet',
          'Archiviert: Klient ist aus der aktiven Liste entfernt',
        ],
      },
      {
        heading: 'Status ändern',
        paragraphs: [],
        steps: [
          'Öffnen Sie die Klientendetailseite',
          'Klicken Sie auf den aktuellen Status (Badge oben rechts)',
          'Wählen Sie den neuen Status aus dem Dropdown',
          'Die Änderung wird sofort gespeichert und im Audit-Log protokolliert',
        ],
      },
      {
        heading: 'Klienten nach Status filtern',
        paragraphs: [
          'In der Klientenliste können Sie nach Status filtern, um z.B. nur aktive oder nur archivierte Klienten anzuzeigen. So behalten Sie den Überblick auch bei vielen Klienten.',
        ],
      },
    ],
  },

  // --- 58. Dashboard-Metriken verstehen ---
  {
    id: 'dashboard-metriken',
    title: 'Dashboard-Metriken & KPIs verstehen',
    shortTitle: 'Dashboard-Metriken',
    description: 'Was bedeuten die Zahlen auf Ihrem Dashboard? Eine Erklärung aller KPIs und Trends.',
    category: 'erste-schritte',
    icon: 'TrendingUp',
    relatedRoute: '/dashboard',
    keywords: ['dashboard', 'metriken', 'kpi', 'kennzahlen', 'trend', 'umsatz', 'auslastung', 'statistik', 'chart', 'diagramm'],
    estimatedMinutes: 4,
    relatedArticles: ['dashboard-berichte', 'berichte-statistiken', 'schnellstart'],
    sections: [
      {
        heading: 'KPI-Karten',
        paragraphs: [
          'Die vier KPI-Karten oben zeigen Ihre wichtigsten Kennzahlen auf einen Blick: Monatsumsatz, Aktive Klienten, Sitzungen diesen Monat und Offene Beträge.',
        ],
      },
      {
        heading: 'Monatsumsatz',
        paragraphs: [
          'Der Monatsumsatz zeigt die Summe aller im aktuellen Monat gestellten Rechnungen (Status: versendet, bezahlt und überfällig). Das Trend-Badge zeigt die Veränderung zum Vormonat in Prozent.',
        ],
      },
      {
        heading: 'Umsatz-Chart',
        paragraphs: [
          'Das Flächendiagramm zeigt den Umsatzverlauf der letzten 6 Monate. Darunter finden Sie Summary-Cards mit Durchschnitt, Maximum und Gesamtsumme des angezeigten Zeitraums.',
        ],
      },
      {
        heading: 'Quick-Actions & Aktivitäten',
        paragraphs: [
          'Quick-Action-Pills bieten Schnellzugriff auf häufige Aktionen (Neue Sitzung, Neuer Klient, Neue Rechnung). Die Aktivitäten-Timeline zeigt die letzten Aktionen chronologisch: neue Klienten, Sitzungen, Rechnungen und Portal-Aktivitäten.',
        ],
        tip: 'Der Schnellzugriff-Grid unten zeigt wichtige Verknüpfungen zu Modulen, die Sie am häufigsten nutzen.',
      },
    ],
  },

  // --- 59. Klienten-Schnellstart ---
  {
    id: 'klienten-schnellstart',
    title: 'Schnellstart für Klienten (Portal-Onboarding)',
    shortTitle: 'Portal-Onboarding',
    description: 'So funktioniert der Registrierungsprozess für Klienten im Portal.',
    category: 'klienten',
    icon: 'UserPlus',
    relatedRoute: '/portal',
    keywords: ['klient', 'portal', 'onboarding', 'registrierung', 'einladung', 'setup', 'link', 'passwort', 'zugang'],
    estimatedMinutes: 3,
    relatedArticles: ['klienten-portal-komplett', 'portal-einstellungen', 'klient-anlegen'],
    sections: [
      {
        heading: 'Einladung versenden',
        paragraphs: [
          'Wenn Sie einen Klienten zum Portal einladen, erhält dieser eine E-Mail mit einem Setup-Link. Der Link ist 7 Tage gültig.',
        ],
      },
      {
        heading: 'Registrierungsprozess des Klienten',
        paragraphs: [],
        steps: [
          'Klient klickt auf den Setup-Link in der E-Mail',
          'Der 6-stellige Einladungscode wird automatisch ausgefüllt (oder manuell eingeben)',
          'Klient legt ein Passwort fest (mind. 8 Zeichen, Stärke-Anzeige)',
          'Nach der Registrierung erhält der Klient eine Bestätigung per E-Mail',
          'Der Klient kann sich ab sofort am Portal anmelden',
        ],
      },
      {
        heading: 'Probleme bei der Registrierung',
        paragraphs: [
          'Falls der Setup-Link abgelaufen ist: Senden Sie eine neue Einladung über den Portal-Tab im Klientenprofil ("Erneut einladen"). Falls der Klient sein Passwort vergessen hat: Auf der Portal-Login-Seite gibt es einen "Passwort vergessen"-Link.',
        ],
        tip: 'Nach der ersten Anmeldung wird dem Klienten ein temporäres Passwort angezeigt, das er bei der ersten Nutzung ändern sollte.',
      },
    ],
  },

  // --- 60. Support-Tickets von Klienten ---
  {
    id: 'support-tickets-klienten',
    title: 'Klienten-Tickets verwalten',
    shortTitle: 'Klienten-Tickets',
    description: 'So bearbeiten Sie Support-Tickets, die Ihre Klienten über das Portal erstellt haben.',
    category: 'einstellungen',
    icon: 'MessageCircle',
    relatedRoute: '/support',
    keywords: ['klienten-ticket', 'ticket', 'klient', 'support', 'anfrage', 'portal', 'antwort', 'bearbeiten'],
    estimatedMinutes: 2,
    relatedArticles: ['support-tickets', 'klienten-portal-komplett', 'aenderungsantraege'],
    sections: [
      {
        heading: 'Klienten-Tickets einsehen',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Support"',
          'Wechseln Sie zum Tab "Klienten-Tickets"',
          'Sie sehen alle Tickets, die Klienten über das Portal erstellt haben',
          'Klicken Sie auf ein Ticket, um die Details zu sehen',
        ],
      },
      {
        heading: 'Tickets beantworten',
        paragraphs: [
          'Klicken Sie auf "Antworten", um dem Klienten direkt zu antworten. Ihre Antwort wird im Portal angezeigt und der Klient wird per E-Mail benachrichtigt. Sie können den Ticket-Status auf "In Bearbeitung", "Beantwortet" oder "Geschlossen" setzen.',
        ],
        tip: 'Klienten-Tickets sind eine gute Ergänzung zum Chat für formellere Anfragen, die dokumentiert werden sollen.',
      },
    ],
  },

  // --- 61. QM-Dashboard ---
  {
    id: 'qm-dashboard',
    title: 'QM-Dashboard & PDCA-Zyklus',
    shortTitle: 'QM-Dashboard',
    description: 'Das Qualitätsmanagement-Dashboard: Audits, Risiken, Maßnahmen und PDCA im Überblick.',
    category: 'methoden',
    icon: 'BarChart3',
    relatedRoute: '/qualitaet',
    keywords: ['qm', 'qualität', 'dashboard', 'pdca', 'audit', 'risiko', 'maßnahme', 'iso', '9001', 'qualitätsmanagement'],
    estimatedMinutes: 4,
    relatedArticles: ['qualitaetsmanagement'],
    sections: [
      {
        heading: 'Übersicht',
        paragraphs: [
          'Das QM-Dashboard gibt Ihnen einen Überblick über den Stand Ihres Qualitätsmanagements nach ISO 9001. Es zeigt offene Audits, aktive Risiken, laufende Maßnahmen und den PDCA-Fortschritt.',
        ],
      },
      {
        heading: 'PDCA-Zyklus',
        paragraphs: [
          'Der PDCA-Zyklus (Plan-Do-Check-Act) ist das Herzstück des QM-Moduls. Im Dashboard sehen Sie, in welcher Phase sich Ihre QM-Aktivitäten befinden: Planung, Umsetzung, Überprüfung oder Verbesserung.',
        ],
      },
      {
        heading: 'QM-Module im Überblick',
        paragraphs: [],
        steps: [
          'Audits: Interne und externe Qualitätsaudits planen und dokumentieren',
          'Risiken: Risikobewertung und Maßnahmenplanung',
          'Nicht-Konformitäten: Abweichungen erfassen und CAPA-Maßnahmen einleiten',
          'Formulare: QM-Formulare für standardisierte Prozesse',
          'Dokumente: Qualitätsdokumente mit Versionierung und Freigabe-Workflow',
        ],
        tip: 'Das QM-Modul ist besonders relevant, wenn Sie nach ISO 9001 zertifiziert sind oder eine Zertifizierung anstreben.',
      },
    ],
  },

  // --- 62. Private Notizen ---
  {
    id: 'private-notizen',
    title: 'Private Coach-Notizen',
    shortTitle: 'Private Notizen',
    description: 'Erfassen Sie vertrauliche Notizen zu Klienten, die nur für Sie sichtbar sind.',
    category: 'klienten',
    icon: 'Lock',
    relatedRoute: '/klienten',
    keywords: ['notiz', 'notizen', 'privat', 'vertraulich', 'intern', 'coach', 'geheim', 'nur für mich'],
    estimatedMinutes: 2,
    relatedArticles: ['sitzung-notizen-dokumentieren', 'klient-anlegen', 'klienten-portal-komplett'],
    sections: [
      {
        heading: 'Private Notizen erstellen',
        paragraphs: [],
        steps: [
          'Öffnen Sie die Klientendetailseite',
          'Wechseln Sie zum Tab "Notizen"',
          'Klicken Sie auf "+ Neue Notiz"',
          'Schreiben Sie Ihre Notiz und speichern Sie',
        ],
      },
      {
        heading: 'Sichtbarkeit',
        paragraphs: [
          'Private Notizen sind ausschließlich für Sie als Coach sichtbar. Sie erscheinen NICHT im Klienten-Portal, nicht in PDF-Exporten und nicht in der Datenauskunft. Sie dienen ausschließlich Ihrer internen Dokumentation.',
          'Im Team-Modus sind private Notizen auch für Praxis-Owner und Admins sichtbar, aber nicht für andere Coaches.',
        ],
        tip: 'Nutzen Sie private Notizen für Ihre persönlichen Beobachtungen, Hypothesen und Reflexionen zum Coaching-Prozess.',
      },
    ],
  },

  // --- 63. Kalender-Synchronisation ---
  {
    id: 'kalender-sync',
    title: 'Kalender-Synchronisation einrichten',
    shortTitle: 'Kalender-Sync',
    description: 'Synchronisieren Sie Ihren CoachDesk365-Kalender mit Google Calendar, Outlook oder Apple Calendar.',
    category: 'integrationen',
    icon: 'RefreshCw',
    relatedRoute: '/integrationen',
    keywords: ['kalender', 'sync', 'google', 'outlook', 'apple', 'ical', 'ics', 'synchronisation', 'verbinden'],
    estimatedMinutes: 3,
    relatedArticles: ['outlook-kalender-sync', 'integrationen-uebersicht', 'sitzung-planen'],
    sections: [
      {
        heading: 'Synchronisations-Optionen',
        paragraphs: [
          'CoachDesk365 bietet zwei Wege, Ihren Kalender zu synchronisieren: Direkte Verbindung (Zwei-Wege-Sync mit Outlook) oder ICS-Feed (Einweg-Sync, kompatibel mit allen Kalender-Apps).',
        ],
      },
      {
        heading: 'ICS-Feed einrichten',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Integrationen" → "Kalender"',
          'Kopieren Sie die ICS-Feed-URL',
          'Öffnen Sie Ihren Kalender (Google Calendar, Apple Calendar, etc.)',
          'Fügen Sie einen neuen Kalender über URL hinzu',
          'Fügen Sie die kopierte URL ein',
        ],
        tip: 'Der ICS-Feed aktualisiert sich automatisch. Neue Termine erscheinen typischerweise innerhalb von 15-60 Minuten in Ihrem externen Kalender.',
      },
      {
        heading: 'Was wird synchronisiert?',
        paragraphs: [
          'Alle CoachDesk365-Termine (Sitzungen, Buchungen, Blocker) erscheinen in Ihrem externen Kalender. Bei der direkten Outlook-Verbindung werden auch externe Termine in CoachDesk365 angezeigt.',
        ],
      },
    ],
  },

  // --- 64. Buchungsbestätigungen ---
  {
    id: 'buchungsbestaetigungen',
    title: 'Buchungsbestätigungen & Erinnerungen',
    shortTitle: 'Bestätigungen',
    description: 'Automatische E-Mail-Bestätigungen bei neuen Buchungen und Erinnerungen vor dem Termin.',
    category: 'termine',
    icon: 'CheckCircle',
    relatedRoute: '/kalender',
    keywords: ['bestätigung', 'buchungsbestätigung', 'erinnerung', 'email', 'automatisch', 'termin', 'buchung', 'benachrichtigung'],
    estimatedMinutes: 2,
    relatedArticles: ['buchungssystem', 'termin-erinnerungen', 'email-vorlagen'],
    sections: [
      {
        heading: 'Automatische Bestätigungen',
        paragraphs: [
          'Wenn ein Klient über die Buchungsseite oder das Portal einen Termin bucht, erhält er automatisch eine Bestätigungs-E-Mail mit: Datum und Uhrzeit, Format (Online/Vor Ort), Zoom-Link (falls Online) und einer ICS-Datei zum Import in den eigenen Kalender.',
        ],
      },
      {
        heading: 'Terminerinnerungen',
        paragraphs: [
          '24 Stunden vor jedem Termin versendet CoachDesk365 automatisch eine Erinnerungs-E-Mail an den Klienten. Sie als Coach erhalten ebenfalls eine Zusammenfassung Ihrer anstehenden Termine.',
        ],
        tip: 'Stellen Sie sicher, dass bei Ihren Klienten gültige E-Mail-Adressen hinterlegt sind, damit Bestätigungen und Erinnerungen ankommen.',
      },
    ],
  },

  // --- 65. Buchungsseite anpassen ---
  {
    id: 'buchungsseite-anpassen',
    title: 'Öffentliche Buchungsseite anpassen',
    shortTitle: 'Buchungsseite',
    description: 'Personalisieren Sie Ihre öffentliche Buchungsseite mit Ihrem Branding und Ihren Angeboten.',
    category: 'termine',
    icon: 'Globe',
    relatedRoute: '/kalender',
    keywords: ['buchungsseite', 'öffentlich', 'anpassen', 'branding', 'link', 'url', 'slug', 'booking', 'teilen'],
    estimatedMinutes: 3,
    relatedArticles: ['buchungssystem', 'verfuegbarkeiten', 'slot-management', 'white-label'],
    sections: [
      {
        heading: 'Ihre Buchungsseite',
        paragraphs: [
          'Jeder Coach erhält eine eigene, öffentliche Buchungsseite unter: www.coachdesk365.com/buchen/ihr-name. Den Slug (URL-Kürzel) können Sie in den Einstellungen anpassen.',
        ],
      },
      {
        heading: 'Buchungsseite einrichten',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Kalender" → Tab "Buchungen"',
          'Legen Sie Ihre Verfügbarkeiten fest',
          'Definieren Sie die buchbaren Sitzungstypen und Dauern',
          'Passen Sie den Slug an (z.B. /buchen/max-mustermann)',
          'Teilen Sie den Link auf Ihrer Website oder per E-Mail',
        ],
      },
      {
        heading: 'Was sieht der Klient?',
        paragraphs: [
          'Der Klient sieht Ihren Firmennamen, verfügbare Zeitslots und kann direkt einen Termin buchen. Nach der Buchung erhält er eine automatische Bestätigung.',
        ],
        tip: 'Integrieren Sie den Buchungslink auf Ihrer Website als Button, damit potenzielle Klienten direkt einen Termin vereinbaren können.',
      },
    ],
  },

  // --- 66. Session-Vorlagen (Prozessbausteine) ---
  {
    id: 'session-vorlagen',
    title: 'Session-Vorlagen für wiederkehrende Formate',
    shortTitle: 'Session-Vorlagen',
    description: 'Nutzen Sie Vorlagen für Erst-, Folge- und Abschlussgespräche, um Zeit zu sparen.',
    category: 'methoden',
    icon: 'Copy',
    relatedRoute: '/einstellungen',
    keywords: ['vorlage', 'session', 'sitzung', 'template', 'wiederkehrend', 'format', 'erstgespräch', 'abschluss'],
    estimatedMinutes: 3,
    relatedArticles: ['prozessbausteine', 'sitzung-anlegen', 'notiz-vorlagen', 'custom-methods'],
    sections: [
      {
        heading: 'Vorlagen-Typen',
        paragraphs: [
          'CoachDesk365 unterscheidet verschiedene Sitzungstypen, die jeweils eine eigene Dokumentationsstruktur mitbringen: Erstgespräch, reguläre Sitzung, Follow-Up und Abschlussgespräch. Jeder Typ enthält vordefinierte Sektionen.',
        ],
      },
      {
        heading: 'Vorlagen in Einstellungen anpassen',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Einstellungen"',
          'Wechseln Sie zum Bereich "Paket-Vorlagen" oder "Notiz-Vorlagen"',
          'Erstellen oder bearbeiten Sie eine Vorlage',
          'Definieren Sie die Standardsektionen und ggf. Standardtexte',
          'Speichern Sie die Vorlage',
        ],
      },
      {
        heading: 'Vorlagen verwenden',
        paragraphs: [
          'Beim Anlegen eines neuen Prozessbausteins können Sie eine Vorlage auswählen. Die vordefinierten Felder werden automatisch ausgefüllt. Sie können danach alles individuell anpassen.',
        ],
        tip: 'Erstellen Sie separate Vorlagen für verschiedene Coaching-Formate (Einzelcoaching, Gruppencoaching, Workshops), um den Dokumentationsaufwand zu minimieren.',
      },
    ],
  },

  // --- 67. Coaching-Paket-Vorlagen ---
  {
    id: 'paket-vorlagen',
    title: 'Coaching-Paket-Vorlagen erstellen',
    shortTitle: 'Paket-Vorlagen',
    description: 'Erstellen Sie wiederverwendbare Vorlagen für Coaching-Pakete mit festen Konditionen.',
    category: 'finanzen',
    icon: 'Package',
    relatedRoute: '/einstellungen',
    keywords: ['paket', 'vorlage', 'template', 'coaching-paket', 'standard', 'wiederverwendbar', 'konditionen'],
    estimatedMinutes: 3,
    relatedArticles: ['coaching-pakete', 'rechnung-erstellen', 'angebote-verwalten'],
    sections: [
      {
        heading: 'Paket-Vorlage erstellen',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Einstellungen" → "Paket-Vorlagen"',
          'Klicken Sie auf "+ Neue Vorlage"',
          'Definieren Sie: Name, Beschreibung, Anzahl Sitzungen, Gesamtpreis, Sitzungsdauer',
          'Speichern Sie die Vorlage',
        ],
      },
      {
        heading: 'Vorlage anwenden',
        paragraphs: [
          'Beim Erstellen eines neuen Pakets für einen Klienten können Sie eine gespeicherte Vorlage auswählen. Alle Felder werden automatisch ausgefüllt. Der Preis und die Sitzungszahl können pro Klient angepasst werden.',
        ],
        tip: 'Erstellen Sie Vorlagen für Ihre Standardpakete (z.B. "10er-Karte Einzelcoaching", "Führungskräfte-Programm 6 Monate"), um die Paketanlage zu beschleunigen.',
      },
    ],
  },

  // --- 68. Nachrichten archivieren ---
  {
    id: 'nachrichten-archivieren',
    title: 'Nachrichten & Konversationen archivieren',
    shortTitle: 'Chat archivieren',
    description: 'Halten Sie Ihren Posteingang sauber: Archivieren Sie alte Konversationen manuell oder automatisch.',
    category: 'nachrichten',
    icon: 'Archive',
    relatedRoute: '/nachrichten',
    keywords: ['archiv', 'archivieren', 'chat', 'konversation', 'aufräumen', 'posteingang', 'nachrichten', 'automatisch'],
    estimatedMinutes: 2,
    relatedArticles: ['nachrichten-messaging', 'archivieren'],
    sections: [
      {
        heading: 'Manuell archivieren',
        paragraphs: [],
        steps: [
          'Öffnen Sie "Nachrichten"',
          'Fahren Sie mit der Maus über eine Konversation',
          'Klicken Sie auf das Archiv-Symbol',
          'Die Konversation verschwindet aus dem Posteingang',
        ],
      },
      {
        heading: 'Automatische Archivierung',
        paragraphs: [
          'Konversationen, in denen 90 Tage lang keine neue Nachricht eingegangen ist, werden automatisch archiviert. Diese Automatik läuft im Hintergrund und hält Ihren Posteingang schlank.',
        ],
      },
      {
        heading: 'Archivierte Konversationen anzeigen',
        paragraphs: [
          'Im Nachrichtenbereich können Sie über den Filter "Archiv" alle archivierten Konversationen einsehen. Eine archivierte Konversation wird automatisch wieder aktiv, sobald eine neue Nachricht eingeht.',
        ],
        tip: 'Archivierte Konversationen werden nicht gelöscht. Sie können jederzeit darauf zugreifen und die Konversation wieder aktivieren.',
      },
    ],
  },

  // --- 69. ICS-Kalenderexport ---
  {
    id: 'ical-export',
    title: 'ICS-Kalenderexport: Termine in externen Kalender einbinden',
    shortTitle: 'iCal-Export',
    description: 'Exportieren Sie Ihre CoachDesk365-Termine als iCal-Feed und binden Sie sie in Google Calendar, Outlook oder Apple Kalender ein.',
    category: 'integrationen',
    icon: 'Calendar',
    relatedRoute: '/integrationen',
    keywords: ['ical', 'ics', 'kalender', 'export', 'feed', 'google calendar', 'outlook', 'apple', 'abonnieren', 'synchronisieren'],
    estimatedMinutes: 3,
    relatedArticles: ['kalender-sync', 'outlook-kalender-sync', 'verfuegbarkeiten'],
    sections: [
      {
        heading: 'iCal-Feed-URL kopieren',
        paragraphs: [
          'CoachDesk365 stellt Ihnen eine persönliche iCal-Feed-URL bereit. Über diese URL können externe Kalender-Apps Ihre Termine automatisch abrufen und anzeigen.',
        ],
        steps: [
          'Navigieren Sie zu "Integrationen"',
          'Scrollen Sie zum Bereich "Kalender-Export"',
          'Klicken Sie auf "ICS-Feed-URL kopieren"',
          'Die URL wird in Ihre Zwischenablage kopiert',
        ],
      },
      {
        heading: 'Feed in externen Kalender einbinden',
        paragraphs: [
          'Fügen Sie die kopierte URL als Kalenderabonnement in Ihrer bevorzugten Kalender-App ein. Die Termine werden automatisch synchronisiert.',
        ],
        steps: [
          'Google Calendar: Einstellungen → "Weitere Kalender" → "Per URL" → URL einfügen',
          'Apple Kalender: Ablage → "Neues Kalenderabonnement" → URL einfügen',
          'Outlook: Kalender → "Aus dem Internet" → URL einfügen',
        ],
        tip: 'Der iCal-Feed wird regelmäßig aktualisiert. Neue Termine erscheinen automatisch in Ihrem externen Kalender.',
      },
    ],
  },

  // --- 70. Webhooks konfigurieren ---
  {
    id: 'webhooks-konfigurieren',
    title: 'Webhooks für externe Systeme einrichten',
    shortTitle: 'Webhooks',
    description: 'Richten Sie Webhooks ein, um externe Systeme bei bestimmten Ereignissen automatisch zu benachrichtigen.',
    category: 'integrationen',
    icon: 'Plug',
    relatedRoute: '/integrationen',
    keywords: ['webhook', 'api', 'extern', 'integration', 'automatisierung', 'benachrichtigung', 'event', 'trigger', 'endpoint'],
    estimatedMinutes: 4,
    relatedArticles: ['integrationen-uebersicht', 'whatsapp-teams-einrichten'],
    sections: [
      {
        heading: 'Was sind Webhooks?',
        paragraphs: [
          'Webhooks sind automatische Benachrichtigungen, die CoachDesk365 an eine von Ihnen definierte URL sendet, wenn ein bestimmtes Ereignis eintritt -- z.B. wenn ein neuer Klient angelegt oder eine Rechnung bezahlt wird.',
        ],
      },
      {
        heading: 'Webhook anlegen',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Integrationen"',
          'Öffnen Sie den Bereich "Webhooks"',
          'Klicken Sie auf "+ Neuer Webhook"',
          'Geben Sie die Ziel-URL ein (Ihr externer Endpunkt)',
          'Wählen Sie die gewünschten Ereignisse aus (z.B. "Klient erstellt", "Rechnung bezahlt")',
          'Speichern Sie den Webhook',
        ],
        tip: 'Testen Sie Ihren Webhook mit einem Testaufruf, bevor Sie ihn produktiv nutzen. So stellen Sie sicher, dass Ihr Endpunkt korrekt antwortet.',
      },
      {
        heading: 'Fehlerbehandlung',
        paragraphs: [
          'Falls ein Webhook fehlschlägt, versucht CoachDesk365 automatisch bis zu drei erneute Zustellungen. Im Webhook-Protokoll sehen Sie den Status und eventuelle Fehlermeldungen.',
        ],
      },
    ],
  },

  // --- 71. Berichte exportieren ---
  {
    id: 'berichte-exportieren',
    title: 'Berichte als PDF exportieren',
    shortTitle: 'Berichte exportieren',
    description: 'Exportieren Sie Ihre Coaching-Berichte und Statistiken als professionelle PDF-Dokumente.',
    category: 'erste-schritte',
    icon: 'Download',
    relatedRoute: '/berichte',
    keywords: ['bericht', 'export', 'pdf', 'statistik', 'auswertung', 'herunterladen', 'drucken'],
    estimatedMinutes: 2,
    relatedArticles: ['berichte-statistiken', 'dashboard-berichte', 'datev-export'],
    sections: [
      {
        heading: 'Bericht erstellen und exportieren',
        paragraphs: [
          'CoachDesk365 bietet verschiedene Berichtstypen, die Sie als PDF herunterladen können -- von Umsatzberichten bis zu Klientenübersichten.',
        ],
        steps: [
          'Navigieren Sie zu "Berichte"',
          'Wählen Sie den gewünschten Berichtstyp',
          'Legen Sie den Zeitraum fest',
          'Klicken Sie auf "Als PDF exportieren"',
        ],
      },
      {
        heading: 'Berichte anpassen',
        paragraphs: [
          'Vor dem Export können Sie Filter setzen, um nur bestimmte Klienten, Zeiträume oder Kategorien einzubeziehen. Der exportierte PDF-Bericht enthält Ihr Logo und Ihre Geschäftsdaten.',
        ],
        tip: 'Nutzen Sie die Berichtsfunktion regelmäßig, um Ihren Coaching-Erfolg zu dokumentieren und gegenüber Auftraggebern nachzuweisen.',
      },
    ],
  },

  // --- 72. Aktivitäts-Timeline ---
  {
    id: 'aktivitaets-timeline',
    title: 'Aktivitäts-Timeline im Dashboard verstehen',
    shortTitle: 'Aktivitäts-Timeline',
    description: 'Behalten Sie den Überblick über alle aktuellen Aktivitäten direkt auf Ihrem Dashboard.',
    category: 'erste-schritte',
    icon: 'Clock',
    relatedRoute: '/dashboard',
    keywords: ['timeline', 'aktivität', 'dashboard', 'übersicht', 'feed', 'neuigkeiten', 'verlauf', 'chronik'],
    estimatedMinutes: 2,
    relatedArticles: ['schnellstart', 'dashboard-berichte', 'dashboard-metriken'],
    sections: [
      {
        heading: 'Was zeigt die Timeline?',
        paragraphs: [
          'Die Aktivitäts-Timeline auf Ihrem Dashboard listet chronologisch alle wichtigen Ereignisse auf: neue Klienten, geplante Sitzungen, versendete Rechnungen, eingegangene Zahlungen und mehr.',
          'So sehen Sie auf einen Blick, was zuletzt passiert ist -- ohne durch verschiedene Menüpunkte navigieren zu müssen.',
        ],
      },
      {
        heading: 'Timeline lesen und nutzen',
        paragraphs: [
          'Jeder Eintrag zeigt das Ereignis, den Zeitpunkt und ggf. den zugehörigen Klienten. Klicken Sie auf einen Eintrag, um direkt zum betreffenden Element zu springen.',
        ],
        tip: 'Die Timeline aktualisiert sich automatisch. Sie zeigt die letzten Aktivitäten der vergangenen Tage an.',
      },
    ],
  },

  // --- 73. Eigene Support-Tickets ---
  {
    id: 'support-tickets-eigene',
    title: 'Eigene Support-Tickets verwalten',
    shortTitle: 'Eigene Tickets',
    description: 'Erstellen und verfolgen Sie Ihre eigenen Support-Anfragen an das CoachDesk365-Team.',
    category: 'einstellungen',
    icon: 'MessageSquare',
    relatedRoute: '/support',
    keywords: ['support', 'ticket', 'hilfe', 'anfrage', 'problem', 'melden', 'eigene', 'status'],
    estimatedMinutes: 2,
    relatedArticles: ['support-tickets', 'support-tickets-klienten', 'aenderungsantraege'],
    sections: [
      {
        heading: 'Neues Ticket erstellen',
        paragraphs: [
          'Wenn Sie Hilfe benötigen oder ein Problem melden möchten, erstellen Sie ein Support-Ticket direkt aus CoachDesk365 heraus.',
        ],
        steps: [
          'Navigieren Sie zu "Support"',
          'Wechseln Sie zum Tab "Eigene Tickets"',
          'Klicken Sie auf "+ Neues Ticket"',
          'Beschreiben Sie Ihr Anliegen möglichst genau',
          'Wählen Sie die Priorität (niedrig, mittel, hoch)',
          'Senden Sie das Ticket ab',
        ],
      },
      {
        heading: 'Ticket-Status verfolgen',
        paragraphs: [
          'Unter "Eigene Tickets" sehen Sie den aktuellen Status jedes Tickets: offen, in Bearbeitung oder gelöst. Sie erhalten eine E-Mail-Benachrichtigung, sobald eine Antwort vorliegt.',
        ],
        tip: 'Fügen Sie Screenshots hinzu, wenn Sie einen Fehler melden. Das beschleunigt die Bearbeitung erheblich.',
      },
    ],
  },

  // --- 74. QM: Audits ---
  {
    id: 'qm-audits',
    title: 'QM: Audits planen und durchführen',
    shortTitle: 'QM-Audits',
    description: 'Planen Sie interne Audits nach ISO 9001 und dokumentieren Sie Ergebnisse strukturiert.',
    category: 'methoden',
    icon: 'ClipboardCheck',
    relatedRoute: '/qualitaet',
    keywords: ['audit', 'qualität', 'qm', 'iso', '9001', 'prüfung', 'intern', 'planen', 'dokumentieren'],
    estimatedMinutes: 4,
    relatedArticles: ['qualitaetsmanagement', 'qm-dashboard', 'qm-risiken', 'qm-capa'],
    sections: [
      {
        heading: 'Audit planen',
        paragraphs: [
          'Interne Audits helfen Ihnen, die Qualität Ihrer Coaching-Prozesse systematisch zu überprüfen und Verbesserungspotenziale zu identifizieren.',
        ],
        steps: [
          'Navigieren Sie zu "Qualität"',
          'Wechseln Sie zum Bereich "Audits"',
          'Klicken Sie auf "+ Neues Audit"',
          'Wählen Sie den Audit-Typ und den zu prüfenden Prozess',
          'Legen Sie Termin und Verantwortliche fest',
        ],
      },
      {
        heading: 'Audit durchführen und dokumentieren',
        paragraphs: [
          'Während des Audits erfassen Sie Befunde direkt in CoachDesk365. Jeder Befund wird kategorisiert (Konformität, Abweichung, Verbesserungsvorschlag) und kann mit Maßnahmen verknüpft werden.',
        ],
        tip: 'Verknüpfen Sie Audit-Befunde mit CAPA-Maßnahmen, um die Nachverfolgung sicherzustellen.',
      },
    ],
  },

  // --- 75. QM: Risikobewertung ---
  {
    id: 'qm-risiken',
    title: 'QM: Risikobewertung und Maßnahmen',
    shortTitle: 'QM-Risiken',
    description: 'Bewerten Sie Risiken in Ihren Coaching-Prozessen und definieren Sie geeignete Gegenmaßnahmen.',
    category: 'methoden',
    icon: 'AlertTriangle',
    relatedRoute: '/qualitaet',
    keywords: ['risiko', 'bewertung', 'qualität', 'qm', 'maßnahme', 'gefahr', 'wahrscheinlichkeit', 'auswirkung'],
    estimatedMinutes: 3,
    relatedArticles: ['qualitaetsmanagement', 'qm-audits', 'qm-capa'],
    sections: [
      {
        heading: 'Risiken identifizieren',
        paragraphs: [
          'Im QM-Modul können Sie potenzielle Risiken für Ihre Coaching-Praxis erfassen -- von Datenschutzrisiken bis hin zu Prozesslücken.',
        ],
        steps: [
          'Navigieren Sie zu "Qualität"',
          'Öffnen Sie den Bereich "Risiken"',
          'Klicken Sie auf "+ Neues Risiko"',
          'Beschreiben Sie das Risiko und bewerten Sie Eintrittswahrscheinlichkeit sowie Auswirkung',
        ],
      },
      {
        heading: 'Maßnahmen ableiten',
        paragraphs: [
          'Für jedes Risiko können Sie Gegenmaßnahmen definieren und Verantwortliche zuweisen. Die Risikomatrix zeigt Ihnen auf einen Blick, welche Risiken priorisiert behandelt werden sollten.',
        ],
        tip: 'Überprüfen Sie Ihre Risikobewertungen regelmäßig -- mindestens einmal pro Quartal -- und passen Sie die Maßnahmen bei Bedarf an.',
      },
    ],
  },

  // --- 76. QM: CAPA ---
  {
    id: 'qm-capa',
    title: 'QM: CAPA-Maßnahmen bei Nicht-Konformität',
    shortTitle: 'QM-CAPA',
    description: 'Erstellen Sie Korrektur- und Vorbeugemaßnahmen (CAPA) bei Abweichungen und verfolgen Sie deren Umsetzung.',
    category: 'methoden',
    icon: 'ShieldCheck',
    relatedRoute: '/qualitaet',
    keywords: ['capa', 'korrektur', 'vorbeugung', 'maßnahme', 'abweichung', 'nicht-konformität', 'qualität', 'verbesserung'],
    estimatedMinutes: 3,
    relatedArticles: ['qualitaetsmanagement', 'qm-audits', 'qm-risiken'],
    sections: [
      {
        heading: 'CAPA-Maßnahme anlegen',
        paragraphs: [
          'CAPA steht für Corrective and Preventive Action. Wenn bei einem Audit oder im laufenden Betrieb eine Abweichung festgestellt wird, erstellen Sie eine CAPA-Maßnahme.',
        ],
        steps: [
          'Navigieren Sie zu "Qualität"',
          'Öffnen Sie den Bereich "CAPA"',
          'Klicken Sie auf "+ Neue CAPA-Maßnahme"',
          'Wählen Sie den Typ: Korrekturmaßnahme oder Vorbeugemaßnahme',
          'Beschreiben Sie die Ursache und die geplante Maßnahme',
          'Legen Sie Frist und Verantwortliche fest',
        ],
      },
      {
        heading: 'Umsetzung verfolgen',
        paragraphs: [
          'Jede CAPA-Maßnahme durchläuft einen Status-Workflow: offen → in Bearbeitung → umgesetzt → verifiziert. So stellen Sie sicher, dass keine Maßnahme vergessen wird.',
        ],
        tip: 'Verknüpfen Sie CAPA-Maßnahmen mit den ursprünglichen Audit-Befunden, um die Rückverfolgbarkeit zu gewährleisten.',
      },
    ],
  },

  // --- 77. QM: Formulare ---
  {
    id: 'qm-formulare',
    title: 'QM: Formulare ausfüllen und verwalten',
    shortTitle: 'QM-Formulare',
    description: 'Nutzen Sie standardisierte QM-Formulare für Checklisten, Bewertungen und Dokumentationen.',
    category: 'methoden',
    icon: 'FileText',
    relatedRoute: '/qualitaet',
    keywords: ['formular', 'checkliste', 'qm', 'qualität', 'ausfüllen', 'dokumentation', 'standard', 'vorlage'],
    estimatedMinutes: 2,
    relatedArticles: ['qualitaetsmanagement', 'qm-audits'],
    sections: [
      {
        heading: 'Formulare im QM-Modul',
        paragraphs: [
          'CoachDesk365 stellt verschiedene QM-Formulare bereit, die Sie für wiederkehrende Aufgaben nutzen können -- z.B. Audit-Checklisten, Zufriedenheitsbefragungen oder Prozessdokumentationen.',
        ],
        steps: [
          'Navigieren Sie zu "Qualität"',
          'Öffnen Sie den Bereich "Formulare"',
          'Wählen Sie ein Formular aus oder erstellen Sie ein neues',
          'Füllen Sie die Felder aus und speichern Sie',
        ],
      },
      {
        heading: 'Formulare wiederverwenden',
        paragraphs: [
          'Ausgefüllte Formulare werden archiviert und sind jederzeit abrufbar. Sie können Formulare auch als Vorlage duplizieren, um sie für verschiedene Anlässe anzupassen.',
        ],
        tip: 'Nutzen Sie QM-Formulare systematisch, um bei einer ISO-9001-Zertifizierung die geforderte Dokumentation nachweisen zu können.',
      },
    ],
  },

  // --- 78. Methoden-Arbeitsblätter ---
  {
    id: 'method-worksheets',
    title: 'Methoden-Arbeitsblätter nutzen und drucken',
    shortTitle: 'Arbeitsblätter',
    description: 'Verwenden Sie druckbare Arbeitsblätter zu Coaching-Methoden für Ihre Sitzungen.',
    category: 'methoden',
    icon: 'FileEdit',
    relatedRoute: '/methoden',
    keywords: ['arbeitsblatt', 'worksheet', 'drucken', 'methode', 'vorlage', 'übung', 'coaching', 'material'],
    estimatedMinutes: 2,
    relatedArticles: ['methoden-nutzen', 'custom-methods', 'session-vorlagen'],
    sections: [
      {
        heading: 'Arbeitsblätter finden',
        paragraphs: [
          'Viele Methoden in der Bibliothek enthalten druckbare Arbeitsblätter, die Sie in Ihren Sitzungen mit Klienten verwenden können.',
        ],
        steps: [
          'Navigieren Sie zu "Methoden"',
          'Öffnen Sie eine Methode mit Arbeitsblatt-Symbol',
          'Klicken Sie auf "Arbeitsblatt anzeigen"',
          'Drucken Sie das Arbeitsblatt oder laden Sie es als PDF herunter',
        ],
      },
      {
        heading: 'Arbeitsblätter in Sitzungen einsetzen',
        paragraphs: [
          'Sie können Arbeitsblätter auch direkt einer Sitzung zuordnen. So haben Sie alle verwendeten Materialien zentral im Klientenprofil dokumentiert.',
        ],
        tip: 'Eigene Arbeitsblätter können Sie über "Eigene Methoden" hochladen und mit Ihren Coaching-Methoden verknüpfen.',
      },
    ],
  },

  // --- 79. Admin: Nutzer verwalten ---
  {
    id: 'admin-users',
    title: 'Admin: Nutzer verwalten, aktivieren, löschen',
    shortTitle: 'Nutzerverwaltung',
    description: 'Verwalten Sie als Administrator alle Nutzerkonten: Aktivierung, Deaktivierung und Löschung.',
    category: 'einstellungen',
    icon: 'Users',
    relatedRoute: '/einstellungen',
    keywords: ['admin', 'nutzer', 'benutzer', 'verwalten', 'aktivieren', 'deaktivieren', 'löschen', 'konto'],
    estimatedMinutes: 3,
    relatedArticles: ['team-management', 'account-einstellungen', 'passwort-sicherheit'],
    sections: [
      {
        heading: 'Nutzerliste einsehen',
        paragraphs: [
          'Als Administrator sehen Sie unter "Einstellungen" → "Nutzerverwaltung" eine Übersicht aller registrierten Konten mit Status, Rolle und letztem Login.',
        ],
      },
      {
        heading: 'Nutzer aktivieren oder deaktivieren',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Einstellungen" → "Nutzerverwaltung"',
          'Klicken Sie auf den gewünschten Nutzer',
          'Ändern Sie den Status auf "Aktiv" oder "Deaktiviert"',
          'Speichern Sie die Änderung',
        ],
        tip: 'Deaktivierte Nutzer können sich nicht mehr einloggen, ihre Daten bleiben aber erhalten. Eine Löschung entfernt alle Nutzerdaten unwiderruflich.',
      },
    ],
  },

  // --- 80. Admin: Wartungsmodus ---
  {
    id: 'admin-wartung',
    title: 'Admin: Wartungsmodus und Health-Checks',
    shortTitle: 'Wartung & Health',
    description: 'Überwachen Sie den Systemstatus, führen Sie Health-Checks durch und aktivieren Sie bei Bedarf den Wartungsmodus.',
    category: 'einstellungen',
    icon: 'Settings',
    relatedRoute: '/einstellungen',
    keywords: ['wartung', 'health', 'check', 'status', 'system', 'monitoring', 'verfügbarkeit', 'admin'],
    estimatedMinutes: 3,
    relatedArticles: ['system-health', 'admin-users', 'admin-announcements'],
    sections: [
      {
        heading: 'Systemstatus prüfen',
        paragraphs: [
          'CoachDesk365 führt regelmäßig automatische Health-Checks durch. Unter "Einstellungen" → "Wartung" sehen Sie den aktuellen Status aller Systemkomponenten.',
        ],
      },
      {
        heading: 'Wartungsmodus aktivieren',
        paragraphs: [
          'Im Wartungsmodus wird Nutzern eine Hinweisseite angezeigt. Aktivieren Sie den Modus nur für geplante Wartungsfenster.',
        ],
        steps: [
          'Navigieren Sie zu "Einstellungen" → "Wartung"',
          'Aktivieren Sie den Schalter "Wartungsmodus"',
          'Geben Sie optional eine Nachricht für Nutzer ein',
          'Bestätigen Sie die Aktivierung',
        ],
        tip: 'Informieren Sie Ihre Nutzer vorab über geplante Wartungsarbeiten -- z.B. per Ankündigung.',
      },
    ],
  },

  // --- 81. Admin: Ankündigungen ---
  {
    id: 'admin-announcements',
    title: 'Admin: Ankündigungen an alle Nutzer senden',
    shortTitle: 'Ankündigungen',
    description: 'Versenden Sie Ankündigungen als In-App-Banner oder E-Mail an alle oder ausgewählte Nutzergruppen.',
    category: 'einstellungen',
    icon: 'MessageSquare',
    relatedRoute: '/einstellungen',
    keywords: ['ankündigung', 'broadcast', 'banner', 'mitteilung', 'admin', 'nachricht', 'alle nutzer', 'benachrichtigung'],
    estimatedMinutes: 2,
    relatedArticles: ['admin-wartung', 'admin-users', 'email-vorlagen'],
    sections: [
      {
        heading: 'Ankündigung erstellen',
        paragraphs: [],
        steps: [
          'Navigieren Sie zu "Einstellungen" → "Ankündigungen" (oder "Admin" → "Ankündigungen")',
          'Klicken Sie auf "+ Neue Ankündigung"',
          'Verfassen Sie Titel und Inhalt',
          'Wählen Sie die Zielgruppe (alle Nutzer, nur Coaches, nur Klienten)',
          'Wählen Sie den Kanal: In-App-Banner, E-Mail oder beides',
          'Veröffentlichen Sie die Ankündigung',
        ],
      },
      {
        heading: 'Ankündigungen verwalten',
        paragraphs: [
          'Bereits gesendete Ankündigungen können Sie archivieren oder löschen. In-App-Banner werden automatisch nach dem eingestellten Zeitraum ausgeblendet.',
        ],
        tip: 'Nutzen Sie Ankündigungen sparsam, damit sie von Ihren Nutzern ernst genommen werden. Für dringende Themen wählen Sie E-Mail und Banner gleichzeitig.',
      },
    ],
  },

  // --- 82. Rechnungs-Versionierung ---
  {
    id: 'rechnung-versionierung',
    title: 'Rechnungs-Versionierung und Audit-Historie',
    shortTitle: 'Rechnungshistorie',
    description: 'Verfolgen Sie alle Änderungen an Rechnungen über die Versionierung und Audit-Historie.',
    category: 'finanzen',
    icon: 'GitBranch',
    relatedRoute: '/rechnungen',
    keywords: ['rechnung', 'version', 'historie', 'audit', 'änderung', 'nachvollziehen', 'protokoll', 'versionierung'],
    estimatedMinutes: 2,
    relatedArticles: ['rechnung-erstellen', 'rechnungen-steuern', 'storno-rechnung'],
    sections: [
      {
        heading: 'Änderungshistorie einsehen',
        paragraphs: [
          'Jede Änderung an einer Rechnung -- vom Erstellen über Statuswechsel bis zur Stornierung -- wird protokolliert. So können Sie jederzeit nachvollziehen, wer wann was geändert hat.',
        ],
        steps: [
          'Öffnen Sie die gewünschte Rechnung',
          'Scrollen Sie zum Bereich "Historie" oder "Audit-Log"',
          'Sehen Sie alle Einträge mit Zeitstempel und Aktion',
        ],
      },
      {
        heading: 'GoBD-Konformität',
        paragraphs: [
          'Die Versionierung stellt sicher, dass Ihre Rechnungsstellung GoBD-konform ist. Einmal versendete Rechnungen können nicht mehr verändert werden -- nur storniert und neu erstellt.',
        ],
        tip: 'Für steuerliche Prüfungen können Sie die gesamte Rechnungshistorie als CSV exportieren.',
      },
    ],
  },

  // --- 83. Methoden nach Kategorien ---
  {
    id: 'methoden-kategorien',
    title: 'Methoden nach Kategorien filtern und organisieren',
    shortTitle: 'Methoden-Kategorien',
    description: 'Finden Sie die passende Coaching-Methode schneller durch Kategorie-Filter und Suchfunktion.',
    category: 'methoden',
    icon: 'LayoutGrid',
    relatedRoute: '/methoden',
    keywords: ['methode', 'kategorie', 'filter', 'suche', 'sortieren', 'finden', 'bibliothek', 'organisieren'],
    estimatedMinutes: 2,
    relatedArticles: ['methoden-nutzen', 'custom-methods', 'method-worksheets'],
    sections: [
      {
        heading: 'Methoden filtern',
        paragraphs: [
          'Die Methoden-Bibliothek enthält über 40 Coaching-Methoden in 15 Kategorien. Nutzen Sie die Filter, um schnell die passende Methode zu finden.',
        ],
        steps: [
          'Navigieren Sie zu "Methoden"',
          'Nutzen Sie die Kategorie-Leiste oben, um nach Themengebiet zu filtern',
          'Verwenden Sie die Suchleiste für gezielte Suchen nach Name oder Stichwort',
        ],
      },
      {
        heading: 'Methoden organisieren',
        paragraphs: [
          'Sie können Methoden als Favoriten markieren und eigene Methoden bestimmten Kategorien zuordnen. So bauen Sie sich Ihre persönliche Methodensammlung auf.',
        ],
        tip: 'Nutzen Sie den Shop-Bereich, um zusätzliche Methoden-Pakete zu entdecken, die Ihr Repertoire erweitern.',
      },
    ],
  },

  // --- 84. Cookie-Banner ---
  {
    id: 'cookie-banner',
    title: 'Cookie-Banner und DSGVO-Compliance',
    shortTitle: 'Cookie-Banner',
    description: 'Konfigurieren Sie den Cookie-Banner für Ihre Coaching-Plattform und stellen Sie DSGVO-Konformität sicher.',
    category: 'einstellungen',
    icon: 'Shield',
    relatedRoute: '/einstellungen',
    keywords: ['cookie', 'banner', 'dsgvo', 'datenschutz', 'einwilligung', 'consent', 'compliance', 'tracking'],
    estimatedMinutes: 2,
    relatedArticles: ['datenschutz-loeschanfragen', 'dsgvo-export', 'impressum-agb'],
    sections: [
      {
        heading: 'Cookie-Einstellungen konfigurieren',
        paragraphs: [
          'CoachDesk365 zeigt automatisch einen Cookie-Banner an, der Ihre Besucher über die verwendeten Cookies informiert und deren Einwilligung einholt.',
        ],
        steps: [
          'Navigieren Sie zu "Einstellungen" → "Datenschutz"',
          'Passen Sie den Text des Cookie-Banners an',
          'Definieren Sie, welche Cookie-Kategorien angeboten werden (notwendig, Analyse, Marketing)',
          'Speichern Sie die Einstellungen',
        ],
      },
      {
        heading: 'DSGVO-Compliance',
        paragraphs: [
          'Der Cookie-Banner ist so gestaltet, dass er den aktuellen DSGVO-Anforderungen entspricht. Notwendige Cookies sind immer aktiv, optionale Cookies werden erst nach Einwilligung gesetzt.',
        ],
        tip: 'Prüfen Sie regelmäßig, ob neue Dienste eingebunden wurden, die zusätzliche Cookies setzen. Passen Sie den Banner entsprechend an.',
      },
    ],
  },

  // --- 85. Impressum, AGB, Datenschutz ---
  {
    id: 'impressum-agb',
    title: 'Impressum, AGB und Datenschutz anpassen',
    shortTitle: 'Impressum & AGB',
    description: 'Passen Sie Ihre rechtlichen Seiten an: Impressum, AGB, Datenschutzerklärung und AVV.',
    category: 'einstellungen',
    icon: 'FileSignature',
    relatedRoute: '/einstellungen',
    keywords: ['impressum', 'agb', 'datenschutz', 'avv', 'recht', 'legal', 'pflichtangaben', 'geschäftsbedingungen'],
    estimatedMinutes: 2,
    relatedArticles: ['cookie-banner', 'datenschutz-loeschanfragen', 'geschaeftsdaten'],
    sections: [
      {
        heading: 'Rechtliche Seiten bearbeiten',
        paragraphs: [
          'CoachDesk365 stellt Standardvorlagen für Impressum, AGB, Datenschutzerklärung und Auftragsverarbeitungsvertrag (AVV) bereit. Passen Sie diese mit Ihren individuellen Angaben an.',
        ],
        steps: [
          'Navigieren Sie zu "Einstellungen" → "Rechtliches"',
          'Wählen Sie die gewünschte Seite (Impressum, AGB, Datenschutz, AVV)',
          'Bearbeiten Sie die Inhalte im Editor',
          'Speichern und veröffentlichen Sie die Änderungen',
        ],
      },
      {
        heading: 'Pflichtangaben',
        paragraphs: [
          'Stellen Sie sicher, dass Ihr Impressum alle gesetzlich geforderten Pflichtangaben enthält: Name, Anschrift, E-Mail, Telefon, Steuernummer und ggf. Handelsregistereintrag.',
        ],
        tip: 'Lassen Sie Ihre rechtlichen Texte im Zweifel von einem Anwalt prüfen. Die Vorlagen dienen als Ausgangspunkt, ersetzen aber keine Rechtsberatung.',
      },
    ],
  },

  // --- 86. System-Status ---
  {
    id: 'system-health',
    title: 'System-Status und Verfügbarkeit prüfen',
    shortTitle: 'System-Status',
    description: 'Prüfen Sie die aktuelle Verfügbarkeit aller CoachDesk365-Dienste auf der öffentlichen Status-Seite.',
    category: 'einstellungen',
    icon: 'CheckCircle',
    relatedRoute: '/status',
    keywords: ['status', 'system', 'health', 'verfügbarkeit', 'uptime', 'störung', 'incident', 'ausfall'],
    estimatedMinutes: 2,
    relatedArticles: ['admin-wartung', 'admin-announcements'],
    sections: [
      {
        heading: 'Status-Seite aufrufen',
        paragraphs: [
          'Die Status-Seite unter /status zeigt Ihnen in Echtzeit den Zustand aller Systemkomponenten: API, Datenbank, E-Mail-Versand und weitere Dienste.',
          'Grüne Indikatoren bedeuten, dass alles einwandfrei läuft. Gelb weist auf eingeschränkte Leistung hin, Rot auf eine Störung.',
        ],
      },
      {
        heading: 'Incidents und Historie',
        paragraphs: [
          'Auf der Status-Seite finden Sie auch eine Incident-Historie mit vergangenen Störungen, deren Ursache und Behebung. Bei aktiven Incidents werden Sie über den aktuellen Stand informiert.',
        ],
        tip: 'Setzen Sie ein Lesezeichen auf die Status-Seite, damit Sie bei Problemen schnell prüfen können, ob eine bekannte Störung vorliegt.',
      },
    ],
  },

  // --- 87. Changelog ---
  {
    id: 'changelog',
    title: 'Changelog: Neue Features und Updates',
    shortTitle: 'Changelog',
    description: 'Erfahren Sie, welche neuen Funktionen und Verbesserungen in den letzten Updates hinzugefügt wurden.',
    category: 'erste-schritte',
    icon: 'RefreshCw',
    relatedRoute: '/changelog',
    keywords: ['changelog', 'update', 'version', 'neu', 'feature', 'änderung', 'release', 'verbesserung'],
    estimatedMinutes: 2,
    relatedArticles: ['schnellstart', 'system-health'],
    sections: [
      {
        heading: 'Changelog aufrufen',
        paragraphs: [
          'Unter /changelog finden Sie eine chronologische Übersicht aller Änderungen an CoachDesk365 -- von neuen Features über Verbesserungen bis zu Fehlerbehebungen.',
        ],
        steps: [
          'Navigieren Sie zu "Changelog" (über die Fußzeile oder direkt /changelog)',
          'Scrollen Sie durch die Einträge oder filtern Sie nach Kategorie',
          'Klicken Sie auf einen Eintrag für Details',
        ],
      },
      {
        heading: 'Benachrichtigungen über Updates',
        paragraphs: [
          'Bei wichtigen Updates werden Sie über eine In-App-Ankündigung informiert. So verpassen Sie keine neuen Funktionen, die Ihren Arbeitsalltag erleichtern.',
        ],
        tip: 'Schauen Sie regelmäßig im Changelog vorbei, um neue Features zu entdecken, die Ihre Coaching-Praxis weiter optimieren können.',
      },
    ],
  },
];

// --- Search Utility ---

export function searchArticles(query: string): HelpArticle[] {
  if (!query.trim()) return helpArticles;
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);

  return helpArticles
    .map((article) => {
      const searchable = [
        article.title,
        article.shortTitle,
        article.description,
        ...article.keywords,
      ].join(' ').toLowerCase();

      const score = terms.reduce((sum, term) => {
        if (searchable.includes(term)) return sum + 1;
        return sum;
      }, 0);

      return { article, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ article }) => article);
}

// --- Context-aware article suggestions (route → articles) ---

const ROUTE_ARTICLE_MAP: Record<string, string[]> = {
  '/dashboard': ['schnellstart', 'dashboard-berichte', 'dashboard-metriken', 'nachrichten-messaging', 'aktivitaets-timeline'],
  '/klienten': ['klient-anlegen', 'klienten-status', 'faelle-verwalten', 'faelle-baumstruktur', 'archivieren', 'prozessbausteine', 'private-notizen', 'klienten-export', 'klienten-portal-komplett', 'portal-einstellungen', 'klienten-schnellstart', 'dsgvo-export'],
  '/nachrichten': ['nachrichten-messaging', 'nachrichten-archivieren', 'whatsapp-teams-einrichten'],
  '/methoden': ['methoden-nutzen', 'custom-methods', 'notiz-vorlagen', 'session-vorlagen', 'qm-dashboard', 'qualitaetsmanagement', 'method-worksheets', 'methoden-kategorien'],
  '/rechnungen': ['rechnung-erstellen', 'rechnungen-steuern', 'angebote-verwalten', 'storno-rechnung', 'mahnwesen', 'coaching-pakete', 'paket-vorlagen', 'stripe-zahlungen', 'rechnung-versionierung'],
  '/kalender': ['sitzung-planen', 'sitzung-anlegen', 'slot-management', 'verfuegbarkeiten', 'buchungssystem', 'buchungsseite-anpassen', 'buchungsbestaetigungen', 'termin-erinnerungen'],
  '/berichte': ['berichte-statistiken', 'dashboard-berichte', 'datev-export', 'berichte-exportieren'],
  '/einstellungen': ['account-einstellungen', 'geschaeftsdaten', 'email-vorlagen', 'team-management', 'themes-design', 'passwort-sicherheit', 'session-vorlagen', 'paket-vorlagen', 'admin-users', 'admin-wartung', 'admin-announcements', 'cookie-banner', 'impressum-agb'],
  '/integrationen': ['integrationen-uebersicht', 'zoom-verbinden', 'google-drive-sync', 'outlook-kalender-sync', 'kalender-sync', 'email-vorlagen', 'whatsapp-teams-einrichten', 'cloud-speicher', 'ical-export', 'webhooks-konfigurieren'],
  '/mein-konto': ['abo-management', 'geschaeftsdaten', 'security-2fa', 'datenschutz-loeschanfragen', 'white-label', 'themes-design', 'passwort-sicherheit', 'account-einstellungen'],
  '/qualitaet': ['qualitaetsmanagement', 'qm-dashboard', 'qm-audits', 'qm-risiken', 'qm-capa', 'qm-formulare'],
  '/support': ['support-tickets', 'support-tickets-klienten', 'aenderungsantraege', 'klienten-portal-komplett', 'support-tickets-eigene'],
  '/portal': ['klienten-portal-komplett', 'klienten-portal-tabs', 'klienten-schnellstart', 'online-zahlung', 'stripe-zahlungen'],
  '/dokumente': ['dokumente-vertraege', 'vertragsvorlagen-anpassen', 'cloud-speicher'],
  '/angebote': ['angebote-verwalten', 'angebot-erstellen', 'rechnung-erstellen'],
  '/sitzung-anlegen': ['sitzung-anlegen', 'sitzung-notizen-dokumentieren', 'prozessbausteine'],
  '/status': ['system-health'],
  '/changelog': ['changelog'],
};

export function getArticlesForRoute(pathname: string): HelpArticle[] {
  // Match exact or prefix (e.g., /klienten/123 → /klienten)
  const base = '/' + pathname.split('/').filter(Boolean)[0];
  const ids = ROUTE_ARTICLE_MAP[base] || ROUTE_ARTICLE_MAP['/dashboard'] || [];
  return ids
    .map((id) => helpArticles.find((a) => a.id === id))
    .filter((a): a is HelpArticle => !!a);
}

export function getArticleBySlug(slug: string): HelpArticle | undefined {
  return helpArticles.find((a) => a.id === slug);
}

export function getArticlesByCategory(category: HelpCategory): HelpArticle[] {
  return helpArticles.filter((a) => a.category === category);
}
